import type { Lang, Numerals } from '@/stores/ui-store';

/**
 * Minimal bilingual string table. Keyed for type safety; extend as the
 * app grows. A full next-intl/lingui setup is overkill for v0.1 scope.
 */
export const STRINGS = {
  'app.name': { en: 'Jam-e Jam', fa: 'جامِ جم' },
  'app.tagline': {
    en: 'Sovereign intelligence archive',
    fa: 'آرشیو اطلاعاتی مستقل',
  },
  'nav.command-center': { en: 'Command center', fa: 'مرکز فرماندهی' },
  'nav.graph': { en: 'Network', fa: 'شبکه' },
  'nav.investigations': { en: 'Investigations', fa: 'تحقیقات' },
  'nav.dossiers': { en: 'Dossiers', fa: 'پرونده‌ها' },
  'nav.briefs': { en: 'Briefs', fa: 'گزارش‌ها' },
  'nav.query': { en: 'Ask the Cup', fa: 'از جام بپرس' },
  'nav.capital': { en: 'Capital flows', fa: 'جریان سرمایه' },
  'nav.sources': { en: 'Data sources', fa: 'منابع داده' },
  'ask.placeholder': {
    en: 'Ask anything — or type / for commands',
    fa: 'هر سؤالی بپرسید — یا / برای دستورات',
  },
  'ask.thinking': { en: 'Thinking…', fa: '…در حال بررسی' },
  'dossier.overview': { en: 'Overview', fa: 'نمای کلی' },
  'dossier.network': { en: 'Network', fa: 'شبکه' },
  'dossier.sources': { en: 'Sources', fa: 'منابع' },
  'dossier.notes': { en: 'Notes', fa: 'یادداشت‌ها' },
  'dossier.synthesis': { en: 'Synthesis', fa: 'خلاصه' },
  'dossier.positions': { en: 'Positions', fa: 'مناصب' },
  'dossier.relationships': { en: 'Relationships', fa: 'ارتباطات' },
  'dossier.signals': { en: 'Signals', fa: 'سیگنال‌ها' },
  'dossier.timeline': { en: 'Timeline', fa: 'جدول زمانی' },
  'dossier.relationship-strength': { en: 'Strength', fa: 'قدرت' },
  'dossier.no-signals': {
    en: 'No signals recorded yet.',
    fa: 'سیگنالی ثبت نشده است.',
  },
  'dossier.no-positions': {
    en: 'No documented positions.',
    fa: 'منصبی ثبت نشده است.',
  },
  'auth.signin': { en: 'Sign in', fa: 'ورود' },
  'auth.signout': { en: 'Sign out', fa: 'خروج' },
  'auth.signup': { en: 'Sign up', fa: 'ثبت‌نام' },
  'auth.forgot': { en: 'Forgot password?', fa: 'رمز عبور را فراموش کرده‌اید؟' },
  'auth.reset': { en: 'Reset password', fa: 'بازنشانی رمز عبور' },
  'auth.verify': { en: 'Verify email', fa: 'تأیید ایمیل' },
  'auth.google': { en: 'Continue with Google', fa: 'ادامه با گوگل' },
  'auth.email': { en: 'Email', fa: 'ایمیل' },
  'auth.password': { en: 'Password', fa: 'رمز عبور' },
  'auth.confirm-password': { en: 'Confirm password', fa: 'تأیید رمز عبور' },
  'auth.have-account': {
    en: 'Already have an account?',
    fa: 'از قبل حساب دارید؟',
  },
  'auth.no-account': {
    en: "Don't have an account?",
    fa: 'حساب ندارید؟',
  },
  'auth.check-email': {
    en: 'Check your email for a verification link.',
    fa: 'برای تأیید، ایمیل خود را بررسی کنید.',
  },
  'auth.resend': { en: 'Resend verification email', fa: 'ارسال مجدد ایمیل تأیید' },
  'auth.back-to-signin': { en: 'Back to sign in', fa: 'بازگشت به ورود' },
  'common.confidence': { en: 'Confidence', fa: 'اطمینان' },
  'common.source': { en: 'Source', fa: 'منبع' },
  'common.last-generated': { en: 'Last generated', fa: 'آخرین به‌روزرسانی' },
  'common.loading': { en: 'Loading…', fa: '…در حال بارگذاری' },
  'palette.ai-suggestions': {
    en: 'AI suggestions',
    fa: 'پیشنهادهای هوش مصنوعی',
  },
  'palette.ai-thinking': {
    en: 'Finding refinements…',
    fa: '…در حال یافتن پیشنهاد',
  },
  'palette.placeholder': {
    en: 'Search, ask, or type / for commands',
    fa: 'جست‌وجو، سؤال بپرسید، یا / برای دستورات',
  },
  'palette.try': { en: 'Try', fa: 'پیشنهادها' },
  'palette.entities': { en: 'Entities', fa: 'موجودیت‌ها' },
  'palette.add-to-watchlist': {
    en: 'Add to watchlist',
    fa: 'افزودن به لیست مراقبت',
  },
  'watchlist.toggle.add': { en: 'Watch', fa: 'تحت‌نظر' },
  'watchlist.toggle.watching': { en: 'Watching', fa: 'در حال نظارت' },
  'watchlist.toggle.aria-add': {
    en: 'Add to watchlist',
    fa: 'افزودن به لیست مراقبت',
  },
  'watchlist.toggle.aria-remove': {
    en: 'Remove from watchlist',
    fa: 'حذف از لیست مراقبت',
  },
  'command-center.empty.title': {
    en: 'Your command center is quiet.',
    fa: 'مرکز فرماندهی شما ساکت است.',
  },
  'command-center.empty.body': {
    en: 'Start by watching an entity — open any dossier and click the eye. The nightly scan will surface changes here.',
    fa: 'با تحت‌نظر گرفتن یک موجودیت شروع کنید — هر پرونده را باز کنید و روی نشان چشم بزنید. اسکن شبانه، تغییرات را اینجا می‌آورد.',
  },
  'command-center.evidence.title': {
    en: 'Evidence trail',
    fa: 'ردِ شواهد',
  },
  'command-center.threads.title': {
    en: 'Threads',
    fa: 'رشته‌ها',
  },
  'command-center.stats.entities': {
    en: 'entities',
    fa: 'موجودیت‌ها',
  },
  'command-center.stats.relationships': {
    en: 'relationships',
    fa: 'روابط',
  },
  'command-center.stats.sources': {
    en: 'sources',
    fa: 'منابع',
  },
  'command-center.stats.by-label': {
    en: 'Entities by type',
    fa: 'موجودیت‌ها بر حسب نوع',
  },
  'command-center.stats.top-relationships': {
    en: 'Top relationships',
    fa: 'روابط پرتکرار',
  },
  'command-center.stats.live': {
    en: 'Live',
    fa: 'زنده',
  },
  'command-center.stats.watchlist': {
    en: 'Watchlist',
    fa: 'فهرست پایش',
  },
  'command-center.stats.signals': {
    en: 'Active signals',
    fa: 'سیگنال‌های فعال',
  },
  'command-center.stats.density': {
    en: 'Graph density',
    fa: 'چگالی گراف',
  },
  'command-center.stats.density.note': {
    en: 'relationships per entity',
    fa: 'رابطه به ازای هر موجودیت',
  },
  'command-center.stats.unlabeled': {
    en: 'Unlabeled',
    fa: 'بدون برچسب',
  },
  'palette.commands': { en: 'Commands', fa: 'دستورات' },
  'palette.ask': { en: 'Ask', fa: 'پرسش' },
  'palette.nav.graph': { en: 'Go to network', fa: 'رفتن به شبکه' },
  'palette.nav.dossiers': { en: 'Go to dossiers', fa: 'رفتن به پرونده‌ها' },
  'palette.nav.investigations': {
    en: 'Go to investigations',
    fa: 'رفتن به تحقیقات',
  },
  'palette.cmd.theme': { en: 'Toggle theme', fa: 'تغییر پوسته' },
  'palette.cmd.language': { en: 'Toggle language', fa: 'تغییر زبان' },
  'menu.density': { en: 'Density', fa: 'تراکم' },
  'menu.language': { en: 'Language', fa: 'زبان' },
  'menu.theme': { en: 'Theme', fa: 'پوسته' },
  'theme.daylight': { en: 'Daylight', fa: 'روز' },
  'theme.midnight': { en: 'Midnight', fa: 'شب' },
  'theme.system': { en: 'System', fa: 'سیستم' },
  'menu.account': { en: 'Account', fa: 'حساب کاربری' },
  'investigations.title': { en: 'Investigations', fa: 'تحقیقات' },
  'investigations.new': { en: 'New investigation', fa: 'تحقیق جدید' },
  'investigations.empty': {
    en: 'No investigations yet. Create one to start a case file.',
    fa: 'هنوز تحقیقی وجود ندارد. برای شروع پرونده، یکی ایجاد کنید.',
  },
  'investigation.hypothesis': { en: 'Hypothesis', fa: 'فرضیه' },
  'investigation.hypothesis.placeholder': {
    en: 'State your working hypothesis…',
    fa: '…فرضیه خود را بیان کنید',
  },
  'investigation.evidence': { en: 'Evidence', fa: 'شواهد' },
  'investigation.add-evidence': { en: 'Add evidence', fa: 'افزودن شواهد' },
  'investigation.evidence.detail.title': {
    en: 'Evidence details',
    fa: 'جزئیات شواهد',
  },
  'investigation.evidence.detail.claim': { en: 'Claim', fa: 'ادعا' },
  'investigation.evidence.detail.confidence': { en: 'Confidence', fa: 'اطمینان' },
  'investigation.evidence.detail.source': { en: 'Source', fa: 'منبع' },
  'investigation.evidence.detail.added': { en: 'Added', fa: 'افزوده شد' },
  'investigation.linked-entities': {
    en: 'Linked entities',
    fa: 'موجودیت‌های مرتبط',
  },
  'investigation.recommended-actions': {
    en: 'Recommended actions',
    fa: 'اقدامات پیشنهادی',
  },
  'investigation.collab-log': { en: 'Collaboration log', fa: 'گزارش همکاری' },
  'investigation.hypothesis-confidence': {
    en: 'Hypothesis confidence',
    fa: 'اطمینان فرضیه',
  },
  'investigation.archive': { en: 'Archive', fa: 'بایگانی' },
  'investigation.unarchive': {
    en: 'Unarchive',
    fa: 'از بایگانی خارج کردن',
  },
  'investigation.delete': { en: 'Delete', fa: 'حذف' },
  'thread.new': { en: 'New thread', fa: 'گفتگوی جدید' },
  'thread.pin': { en: 'Pin', fa: 'سنجاق کردن' },
  'thread.unpin': { en: 'Unpin', fa: 'برداشتن سنجاق' },
  'thread.fork': { en: 'Fork', fa: 'انشعاب' },
  'thread.rename': { en: 'Rename', fa: 'تغییر نام' },
  'thread.delete': { en: 'Delete', fa: 'حذف' },
  'thread.empty': {
    en: 'No threads yet. Start by asking something.',
    fa: 'هنوز گفتگویی نیست. با یک سؤال شروع کنید.',
  },
  'chat.title': { en: 'Conversation', fa: 'گفتگو' },
  'chat.title.dossier': { en: 'Discuss this dossier', fa: 'بحث درباره این پرونده' },
  'chat.title.graph': { en: 'Discuss this graph', fa: 'بحث درباره این گراف' },
  'chat.empty.dossier': {
    en: 'Ask anything about this dossier — relationships, sourcing, gaps, next steps.',
    fa: 'هر چیزی درباره این پرونده بپرس — ارتباطات، منابع، فاصله‌ها، گام‌های بعدی.',
  },
  'chat.empty.graph': {
    en: 'Ask anything about this graph view — paths, hubs, hidden communities.',
    fa: 'هر چیزی درباره این نمای گراف بپرس — مسیرها، مرکزها، اجتماعات پنهان.',
  },
  'chat.empty.free': {
    en: 'Ask anything across the whole corpus — entities, relationships, evidence gaps.',
    fa: 'هر چیزی در سراسر مجموعه بپرس — موجودیت‌ها، ارتباطات، فاصله‌های مستندی.',
  },
  'chat.placeholder': {
    en: 'Ask a follow-up… (⌘↵ to send)',
    fa: 'سؤال بعدی… (⌘↵ برای ارسال)',
  },
  'chat.send': { en: 'Send', fa: 'ارسال' },
  'chat.stop': { en: 'Stop', fa: 'توقف' },
  'chat.assistant': { en: 'The Cup', fa: 'جام' },
  'chat.threads.title': { en: 'Past conversations', fa: 'گفتگوهای قبلی' },
  'chat.action.applied': { en: 'Done', fa: 'انجام شد' },
  'chat.action.failed': { en: 'Failed', fa: 'ناموفق' },
  'chat.reasoning.label': { en: 'Reasoning · {n}', fa: 'استدلال · {n}' },
  // Agentic-retrieval trace labels (issue #39). Surfaced in the
  // <Reasoning> disclosure as `detail` strings — server emits the
  // step code ("plan" / "iteration" / "tool" / "critique") and the
  // client formats the human-readable detail.
  'chat.reasoning.plan.direct': {
    en: 'Direct retrieval',
    fa: 'بازیابی مستقیم',
  },
  'chat.reasoning.plan.decomposed': {
    en: 'Decomposed into {n} sub-quer{plural}',
    fa: 'تقسیم به {n} زیر-پرسش',
  },
  'chat.reasoning.iteration': {
    en: 'Iteration {n} of {max}',
    fa: 'تکرار {n} از {max}',
  },
  'chat.reasoning.subquery': {
    en: 'Sub-query: {query}',
    fa: 'زیر-پرسش: {query}',
  },
  'chat.reasoning.tool_result': {
    en: '{tool}: +{f} facts / +{s} sources',
    fa: '{tool}: +{f} مورد / +{s} منبع',
  },
  'chat.reasoning.critique.sufficient': {
    en: 'Evidence sufficient (confidence {conf})',
    fa: 'شواهد کافی (اطمینان {conf})',
  },
  'chat.reasoning.critique.gaps': {
    en: 'Gaps remain (confidence {conf}): {gaps}',
    fa: 'شکاف‌ها (اطمینان {conf}): {gaps}',
  },
  'chat.scroll-to-latest': {
    en: 'Scroll to latest message',
    fa: 'پیمایش به آخرین پیام',
  },
  // Per-verb chip messages. {entity} / {a} / {b} are interpolated by
  // tFormat from the action payload; the dispatcher returns the raw
  // verb + entity, never a pre-formatted English string.
  'chat.action.pinEntity.applied': {
    en: 'Pinned {entity} to your watchlist.',
    fa: '{entity} به فهرست پیگیری شما افزوده شد.',
  },
  'chat.action.unpinEntity.applied': {
    en: 'Removed {entity} from your watchlist.',
    fa: '{entity} از فهرست پیگیری شما حذف شد.',
  },
  'chat.action.focusSubgraph.applied': {
    en: 'Focused graph on {entity}.',
    fa: 'گراف بر {entity} تمرکز یافت.',
  },
  'chat.action.openDossier.applied': {
    en: 'Opening {entity} dossier.',
    fa: 'باز کردن پروندهٔ {entity}.',
  },
  'chat.action.compareEntities.applied': {
    en: 'Comparing {a} with {b}.',
    fa: 'مقایسهٔ {a} با {b}.',
  },
  'task.tray.title': { en: 'Active tasks', fa: 'وظایف فعال' },
  'task.tray.empty': { en: 'No active tasks', fa: 'بدون وظیفه فعال' },
  'task.open': { en: 'Open deliverable', fa: 'باز کردن نتیجه' },
  'task.demo': { en: 'Run demo task', fa: 'اجرای وظیفه نمایشی' },
  'task.retry': { en: 'Retry', fa: 'تلاش مجدد' },
  'synthesis.edit': { en: 'Edit', fa: 'ویرایش' },
  'synthesis.edited-by': {
    en: 'Edited by {name}',
    fa: 'ویرایش‌شده توسط {name}',
  },
  'synthesis.save': { en: 'Save', fa: 'ذخیره' },
  'synthesis.cancel': { en: 'Cancel', fa: 'لغو' },
  'synthesis.edit-reason.placeholder': {
    en: 'Optional: why are you correcting this?',
    fa: 'دلیل اصلاح (اختیاری)',
  },
  'brief.save': { en: 'Save', fa: 'ذخیره' },
  'brief.share': { en: 'Share link', fa: 'لینک اشتراک' },
  'brief.link-copied': { en: 'Link copied', fa: 'لینک کپی شد' },
  'brief.recent': { en: 'Recent briefs', fa: 'گزارش‌های اخیر' },
  'brief.empty-recent': {
    en: 'No saved briefs yet.',
    fa: 'هنوز گزارشی ذخیره نشده است.',
  },
  'brief.read-only': { en: 'Read only', fa: 'فقط خواندنی' },
  'brief.edit': { en: 'Edit', fa: 'ویرایش' },
  // Share-panel (briefShare.*) — analyst-side surface for minting + revoking
  // tokenized public links. Public-brief strings live under publicBrief.*.
  'briefShare.panelTitle': {
    en: 'Share with a principal',
    fa: 'اشتراک‌گذاری با مدیر',
  },
  'briefShare.panelDescription': {
    en: 'Generate a read-only link that works without signing in. Revoke any time.',
    fa: 'لینک فقط‌خواندنی بسازید که بدون ورود کار می‌کند. هر زمان خواستید لغو کنید.',
  },
  'briefShare.labelLabel': {
    en: 'Link label (optional)',
    fa: 'برچسب لینک (اختیاری)',
  },
  'briefShare.labelPlaceholder': {
    en: 'e.g. Board read, Apr 2026',
    fa: 'مثلاً مطالعه هیئت، اردیبهشت ۱۴۰۵',
  },
  'briefShare.generateCta': {
    en: 'Generate share link',
    fa: 'ساخت لینک',
  },
  'briefShare.copyLink': { en: 'Copy link', fa: 'کپی لینک' },
  'briefShare.copied': {
    en: 'Copied to clipboard',
    fa: 'در کلیپ‌بورد کپی شد',
  },
  'briefShare.shareLink': {
    en: 'Share link',
    fa: 'پیوند اشتراک‌گذاری',
  },
  'briefShare.activeLinksTitle': {
    en: 'Active share links',
    fa: 'لینک‌های فعال',
  },
  'briefShare.noActiveLinks': {
    en: 'No active share links. Generate one to share this brief with a principal.',
    fa: 'هیچ لینک فعالی وجود ندارد. برای اشتراک‌گذاری با مدیر، یک لینک بسازید.',
  },
  'briefShare.untitledLink': {
    en: 'Untitled link',
    fa: 'لینک بدون عنوان',
  },
  'briefShare.revoke': { en: 'Revoke', fa: 'لغو' },
  'briefShare.revokeConfirm': {
    en: 'Revoke this link? The principal will lose access immediately.',
    fa: 'این لینک لغو شود؟ مدیر بلافاصله دسترسی را از دست می‌دهد.',
  },
  'briefShare.generatedAt': {
    en: 'Created {date}',
    fa: 'ساخته‌شده {date}',
  },
  'briefShare.generateError': {
    en: "Couldn't create share link. Try again.",
    fa: 'ایجاد لینک ممکن نشد. دوباره تلاش کنید.',
  },
  'mfa.enroll.title': {
    en: 'Two-factor authentication',
    fa: 'احراز هویت دو مرحله‌ای',
  },
  'mfa.enroll.subtitle': {
    en: 'Required for analyst-role access. Scan the code with your authenticator app.',
    fa: 'برای دسترسی تحلیلگر الزامی است. کد را با برنامه احراز هویت اسکن کنید.',
  },
  'mfa.enroll.secret-label': {
    en: 'Or enter this secret manually',
    fa: 'یا این رمز را به‌صورت دستی وارد کنید',
  },
  'mfa.enroll.code-label': { en: '6-digit code', fa: 'کد ۶ رقمی' },
  'mfa.enroll.submit': {
    en: 'Complete enrollment',
    fa: 'تکمیل ثبت‌نام',
  },
  'mfa.challenge.title': {
    en: 'Enter your 6-digit code',
    fa: 'کد ۶ رقمی خود را وارد کنید',
  },
  'mfa.challenge.submit': { en: 'Continue', fa: 'ادامه' },
  'nav.er': { en: 'Entity resolution', fa: 'انطباق موجودیت' },
  'er.title': { en: 'Entity resolution queue', fa: 'صف انطباق موجودیت' },
  'er.pending': { en: 'Pending', fa: 'در انتظار' },
  'er.promoted': { en: 'Promoted', fa: 'تأیید شده' },
  'er.rejected': { en: 'Rejected', fa: 'رد شده' },
  'er.promote': { en: 'Promote', fa: 'تأیید' },
  'er.reject': { en: 'Reject', fa: 'رد' },
  'er.unsure': { en: 'Unsure', fa: 'نامشخص' },
  'er.no-candidates': {
    en: 'No candidates in this state.',
    fa: 'موردی در این وضعیت وجود ندارد.',
  },
  'er.candidate-for': { en: 'Candidate for', fa: 'گزینه برای' },
  'er.no-link': { en: 'No confident link', fa: 'بدون پیوند مطمئن' },
  'er.detail.title': { en: 'Candidate details', fa: 'جزئیات گزینه' },
  'er.detail.observed': { en: 'Observed name', fa: 'نام مشاهده‌شده' },
  'er.detail.confidence': { en: 'Confidence', fa: 'اطمینان' },
  'er.detail.derivation': { en: 'Derivation', fa: 'منشأ' },
  'er.detail.source': { en: 'Source', fa: 'منبع' },
  // Streamliner-extended ER queue (issue #36). The page now shows
  // four kinds of review items, not just alias candidates.
  'er.subtitle': {
    en: 'Probabilistic, non-merging. Promotion preserves the original lineage. Streamliner output appears alongside ingest-time candidates.',
    fa: 'احتمالی و بدون ادغام. ارتقاء، شواهد اصلی را حفظ می‌کند. خروجی استریم‌لاینر در کنار گزینه‌های زمان دریافت نمایش داده می‌شود.',
  },
  'er.kind.label': { en: 'Filter by kind', fa: 'فیلتر بر اساس نوع' },
  'er.kind.all': { en: 'All', fa: 'همه' },
  'er.kind.alias': { en: 'Alias', fa: 'هم‌نام' },
  'er.kind.lowConfidenceFact': { en: 'Low-conf fact', fa: 'واقعهٔ کم‌اطمینان' },
  'er.kind.missingEdge': { en: 'Missing edge', fa: 'یال گمشده' },
  'er.kind.redundantSchema': { en: 'Redundant schema', fa: 'طرح‌وارهٔ تکراری' },
  'er.evidence.fact': { en: 'Edge under review', fa: 'یال در حال بررسی' },
  'er.evidence.coMention': { en: 'Frequently co-mentioned', fa: 'هم‌ذکر مکرر' },
  'er.evidence.coMention.detail': {
    en: 'Co-mentioned in {n} documents but no direct edge in the graph.',
    fa: 'در {n} سند هم‌ذکر شده‌اند اما بدون یال مستقیم در گراف.',
  },
  'er.evidence.schema': { en: 'Likely-redundant predicates', fa: 'مسندهای احتمالاً تکراری' },
  'er.evidence.schema.detail': {
    en: 'Occurrences A: {a}, B: {b}. Reason: {reason}.',
    fa: 'تعداد A: {a}, B: {b}. دلیل: {reason}.',
  },
  // Compare (side-by-side dossier).
  'compare.title': { en: 'Compare entities', fa: 'مقایسهٔ موجودیت‌ها' },
  'compare.empty': {
    en: 'Pass two entities via the URL: /compare?a=<name>&b=<name>.',
    fa: 'دو موجودیت را در نشانی وارد کنید: /compare?a=<نام>&b=<نام>.',
  },
  'compare.same-entity': {
    en: 'Cannot compare an entity with itself.',
    fa: 'یک موجودیت را نمی‌توان با خودش مقایسه کرد.',
  },
  'compare.section.facts': { en: 'Facts', fa: 'وقایع' },
  'compare.section.facts.empty': {
    en: 'No facts on this entity.',
    fa: 'برای این موجودیت، واقعه‌ای ثبت نشده است.',
  },
  'compare.section.sources': { en: 'Sources', fa: 'منابع' },
  'compare.section.sources.empty': {
    en: 'No sources on this entity.',
    fa: 'برای این موجودیت، منبعی ثبت نشده است.',
  },
  'compare.shared.title': { en: 'Shared', fa: 'مشترک' },
  'compare.shared.neighbors': { en: 'Common neighbors', fa: 'همسایگان مشترک' },
  'compare.shared.neighbors.empty': {
    en: 'No 1-hop neighbors connect both entities.',
    fa: 'هیچ همسایهٔ یک‌گامی هر دو موجودیت را به هم متصل نمی‌کند.',
  },
  'compare.shared.sources': { en: 'Common sources', fa: 'منابع مشترک' },
  'compare.shared.sources.empty': {
    en: 'No source documents mention both.',
    fa: 'هیچ سندی از هر دو موجودیت یاد نمی‌کند.',
  },
  'compare.action.with': { en: 'Compare with…', fa: 'مقایسه با…' },
  'compare.picker.title': {
    en: 'Compare with another entity',
    fa: 'مقایسه با موجودیت دیگر',
  },
  'compare.picker.description': {
    en: 'Pick a neighbor or type any entity name.',
    fa: 'یک همسایه را انتخاب کنید یا نام موجودیت را تایپ کنید.',
  },
  'compare.picker.placeholder': {
    en: 'Search neighbors or type a name…',
    fa: 'جست‌وجو در همسایگان یا تایپ یک نام…',
  },
  'compare.picker.empty': {
    en: 'No matching neighbor.',
    fa: 'همسایهٔ هم‌خوانی یافت نشد.',
  },
  'compare.picker.compare-with': { en: 'Compare with', fa: 'مقایسه با' },
  // Todos.
  'todos.title': { en: 'Todos', fa: 'فهرست کارها' },
  'todos.subtitle': {
    en: 'Personal task list scoped to this org.',
    fa: 'فهرست کارهای شخصی در محدودهٔ این سازمان.',
  },
  'todos.composer.placeholder': {
    en: 'Add a todo — Enter to save…',
    fa: 'افزودن کار — Enter برای ذخیره…',
  },
  'todos.composer.add': { en: 'Add', fa: 'افزودن' },
  'todos.empty.no-todos': {
    en: 'No todos yet. Add the first one above.',
    fa: 'هنوز کاری ثبت نشده. اولین مورد را در بالا اضافه کنید.',
  },
  'todos.empty.no-match': {
    en: 'No todos match this filter.',
    fa: 'کاری با این فیلتر همخوانی ندارد.',
  },
  'todos.error.title': { en: "Couldn't load todos", fa: 'بارگیری فهرست کارها ممکن نشد' },
  'todos.error.body': {
    en: 'The request failed. Showing the last good list if cached, otherwise nothing yet.',
    fa: 'درخواست با خطا مواجه شد. اگر نسخه‌ای کش شده موجود باشد نمایش داده می‌شود.',
  },
  'todos.filter.all': { en: 'All', fa: 'همه' },
  'todos.filter.todo': { en: 'Open', fa: 'باز' },
  'todos.filter.in_progress': { en: 'In progress', fa: 'در حال انجام' },
  'todos.filter.done': { en: 'Done', fa: 'انجام‌شده' },
  'todos.status.todo': { en: 'open', fa: 'باز' },
  'todos.status.in_progress': { en: 'in progress', fa: 'در حال انجام' },
  'todos.status.done': { en: 'done', fa: 'انجام‌شده' },
  'todos.priority': { en: 'Priority', fa: 'اولویت' },
  'todos.priority.low': { en: 'Low', fa: 'کم' },
  'todos.priority.normal': { en: 'Normal', fa: 'عادی' },
  'todos.priority.high': { en: 'High', fa: 'بالا' },
  'todos.action.advance.todo': { en: 'Re-open', fa: 'باز کردن مجدد' },
  'todos.action.advance.in_progress': { en: 'Mark in-progress', fa: 'علامت در حال انجام' },
  'todos.action.advance.done': { en: 'Mark done', fa: 'علامت انجام‌شده' },
  'todos.action.delete': { en: 'Delete todo', fa: 'حذف کار' },
  'todos.linked.investigation': { en: 'investigation', fa: 'تحقیق' },
  'nav.todos': { en: 'Todos', fa: 'کارها' },
  'nav.map': { en: 'Map', fa: 'نقشه' },
  // Map page.
  'map.title': { en: 'Map', fa: 'نقشه' },
  'map.empty': {
    en: 'No entities have coordinates yet. Add `entity_locations` to your corpus to surface them here.',
    fa: 'هنوز موجودیتی مختصات ندارد. برای نمایش، `entity_locations` را به مجموعه اضافه کنید.',
  },
  'map.error.title': { en: 'Map data unavailable', fa: 'دادهٔ نقشه در دسترس نیست' },
  'map.error.body': {
    en: 'The map entities request failed. Check your connection and try again.',
    fa: 'درخواست موجودیت‌های نقشه با خطا مواجه شد. اتصال خود را بررسی کرده و دوباره تلاش کنید.',
  },
  'map.year-range': { en: 'Active in', fa: 'فعال در' },
  'map.year-range.clear': { en: 'clear', fa: 'پاک‌کردن' },
  'map.showing': { en: 'showing', fa: 'نمایش' },
  'graph.insight.bridge.title': {
    en: 'The structural bridge',
    fa: 'پل ساختاری',
  },
  'graph.insight.bridge.body-1': {
    en: 'Inside this 2-hop subgraph, Trita Parsi sits at the highest betweenness centrality — more shortest paths run through him than any other node.',
    fa: 'در این زیرگراف دو-گامی، تریتا پارسی بالاترین میانجیگری را دارد — بیشترین کوتاه‌ترین مسیرها از میان او می‌گذرد.',
  },
  'graph.insight.bridge.body-2': {
    en: 'He links two clusters that otherwise barely touch: the Quincy Institute think-tank orbit, and the NIAC / NIAC Action advocacy apparatus.',
    fa: 'او دو خوشه را که در غیر این صورت تقریباً بی‌ارتباط‌اند به هم می‌پیوندد: مدار اندیشکده کوئینسی و دستگاه حامی‌گری نایاک و نایاک اکشن.',
  },
  'graph.insight.bridge.body-3': {
    en: 'Remove him and the network fragments — a signature of a structural broker rather than a peripheral participant.',
    fa: 'او را حذف کنید و شبکه از هم می‌پاشد — نشانه‌ای از یک واسط ساختاری، نه یک عضو حاشیه‌ای.',
  },
  'graph.insight.bridge.body-removed': {
    en: 'With Parsi removed, disconnected components remain — the clusters come apart.',
    fa: 'با حذف پارسی، چند مؤلفه مجزا باقی می‌ماند — خوشه‌ها از هم جدا می‌شوند.',
  },
  'graph.preset.label': { en: 'Preset', fa: 'پیش‌تنظیم' },
  'graph.preset.back': {
    en: 'Back to full view',
    fa: 'بازگشت به نمای کامل',
  },
  'graph.node.detail.title': { en: 'Node details', fa: 'جزئیات گره' },
  'graph.node.detail.entity': { en: 'Entity', fa: 'موجودیت' },
  'graph.node.detail.type': { en: 'Type', fa: 'نوع' },
  'graph.node.action.open': { en: 'Open dossier', fa: 'باز کردن پرونده' },
  'graph.node.action.focus': { en: 'Focus on graph', fa: 'تمرکز در گراف' },
  'graph.zoom.toolbar': { en: 'Zoom controls', fa: 'کنترل‌های بزرگ‌نمایی' },
  'graph.zoom.in': { en: 'Zoom in', fa: 'بزرگ‌نمایی' },
  'graph.zoom.out': { en: 'Zoom out', fa: 'کوچک‌نمایی' },
  'graph.zoom.fit': { en: 'Fit to view', fa: 'متناسب با نما' },
  'graph.toolbar.export': { en: 'Export PNG', fa: 'خروجی PNG' },
  'graph.toolbar.lasso': { en: 'Box-select', fa: 'انتخاب چندتایی' },
  'graph.toolbar.lasso.hint': {
    en: 'Hold shift and drag to box-select.',
    fa: 'برای انتخاب گروهی، Shift را نگه دارید و بکشید.',
  },
  'graph.toolbar.group': { en: 'Group by type', fa: 'گروه‌بندی بر اساس نوع' },
  'graph.context.open': { en: 'Open dossier', fa: 'باز کردن پرونده' },
  'graph.context.focus': { en: 'Focus subgraph', fa: 'تمرکز روی زیرگراف' },
  'graph.context.pin': { en: 'Pin position', fa: 'سنجاق کردن' },
  'graph.context.unpin': { en: 'Unpin', fa: 'برداشتن سنجاق' },
  'graph.context.hide': { en: 'Hide node', fa: 'پنهان کردن گره' },
  'graph.context.find-paths': { en: 'Find paths to…', fa: 'یافتن مسیر به…' },
  'graph.context.path-from': { en: 'Set as path "from"', fa: 'تعیین به‌عنوان مبدأ' },
  'graph.context.path-to': { en: 'Set as path "to"', fa: 'تعیین به‌عنوان مقصد' },
  'graph.context.watchlist': { en: 'Add to watchlist', fa: 'افزودن به لیست مراقبت' },
  'graph.context.compare': { en: 'Compare with…', fa: 'مقایسه با…' },
  'graph.bulk.selected': {
    en: '{n} selected',
    fa: '{n} انتخاب‌شده',
  },
  'graph.bulk.clear': { en: 'Clear', fa: 'پاک کردن' },
  'graph.bulk.add-watchlist': {
    en: 'Add all to watchlist',
    fa: 'افزودن همه به لیست مراقبت',
  },
  'graph.bulk.compare': {
    en: 'Compare these two',
    fa: 'مقایسهٔ این دو',
  },
  'graph.bulk.hide': { en: 'Hide selected', fa: 'پنهان کردن منتخب' },
  'graph.bulk.pin': { en: 'Pin selected', fa: 'سنجاق کردن منتخب' },
  'graph.pane.detail': { en: 'Detail', fa: 'جزئیات' },
  'graph.pane.filters': { en: 'Filters', fa: 'فیلترها' },
  'graph.pane.overlay': { en: 'Overlay', fa: 'لایه' },
  'graph.pane.views': { en: 'Views', fa: 'نماها' },
  'graph.filters.labels': { en: 'Entity types', fa: 'نوع موجودیت' },
  'graph.filters.relationships': { en: 'Relationship types', fa: 'نوع روابط' },
  'graph.filters.hidden': { en: 'Hidden nodes', fa: 'گره‌های پنهان' },
  'graph.filters.unhide-all': { en: 'Unhide all', fa: 'نمایش همه' },
  'graph.filters.show-all': { en: 'Show all', fa: 'نمایش همه' },
  'graph.filters.hide-all': { en: 'Hide all', fa: 'پنهان کردن همه' },
  'graph.filters.empty.types': {
    en: 'No entity types in this view.',
    fa: 'در این نما، نوعی موجود نیست.',
  },
  'graph.filters.empty.rels': {
    en: 'No relationship types in this view.',
    fa: 'در این نما، رابطه‌ای موجود نیست.',
  },
  'graph.overlay.title': { en: 'Color overlay', fa: 'لایهٔ رنگی' },
  'graph.overlay.subtitle': {
    en: 'Color and size nodes by a structural metric.',
    fa: 'گره‌ها را بر اساس یک معیار ساختاری رنگ‌آمیزی و اندازه‌بندی می‌کند.',
  },
  'graph.overlay.none': { en: 'Off (by type)', fa: 'خاموش (بر اساس نوع)' },
  'graph.overlay.degree': { en: 'Degree', fa: 'درجه' },
  'graph.overlay.degree.desc': {
    en: 'Direct neighbor count — finds local hubs.',
    fa: 'تعداد همسایه‌های مستقیم — موجودیت‌های مرکزی محلی را پیدا می‌کند.',
  },
  'graph.overlay.betweenness': { en: 'Betweenness', fa: 'میانجیگری' },
  'graph.overlay.betweenness.desc': {
    en: 'Bridge importance — entities that connect clusters.',
    fa: 'اهمیت پل — موجودیت‌هایی که خوشه‌ها را به هم متصل می‌کنند.',
  },
  'graph.overlay.community': { en: 'Communities', fa: 'جوامع' },
  'graph.overlay.community.desc': {
    en: 'Connected-component grouping — discrete clusters get distinct colors.',
    fa: 'گروه‌بندی بر اساس مؤلفه‌های متصل — خوشه‌های مجزا رنگ‌های متفاوت می‌گیرند.',
  },
  'graph.overlay.community.count': {
    en: '{n} groups',
    fa: '{n} گروه',
  },
  'graph.views.title': { en: 'Saved views', fa: 'نماهای ذخیره‌شده' },
  'graph.views.subtitle': {
    en: 'Snapshots of mode, focus, filters, and pinned nodes.',
    fa: 'تصاویر حالت، تمرکز، فیلترها و گره‌های سنجاق‌شده.',
  },
  'graph.views.save': { en: 'Save current view', fa: 'ذخیرهٔ نمای فعلی' },
  'graph.views.name.placeholder': {
    en: 'Name this view…',
    fa: 'نامی برای این نما…',
  },
  'graph.views.empty': {
    en: 'No saved views yet.',
    fa: 'هنوز نمای ذخیره‌شده‌ای نیست.',
  },
  'graph.views.load': { en: 'Load', fa: 'بارگذاری' },
  'graph.views.delete': { en: 'Delete', fa: 'حذف' },
  'graph.views.created': { en: 'Saved {date}', fa: 'ذخیره‌شده {date}' },
  'graph.export.filename': { en: 'graph', fa: 'graph' },
  'graph.search.label': { en: 'Search graph', fa: 'جستجو در گراف' },
  'graph.search.placeholder': {
    en: 'Find an entity by name…',
    fa: 'جستجوی موجودیت بر اساس نام…',
  },
  'graph.search.no-matches': { en: 'no matches', fa: 'بدون نتیجه' },
  'graph.search.close': { en: 'Close search', fa: 'بستن جستجو' },
  'graph.search.open': { en: 'Find on graph', fa: 'جستجو در گراف' },
  'graph.search.open-hint': {
    en: 'Find on graph (press /)',
    fa: 'جستجو در گراف (کلید /)',
  },
  'graph.search.list.label': { en: 'Matched entities', fa: 'موجودیت‌های منطبق' },
  'graph.search.list.open': { en: 'Show match list', fa: 'نمایش فهرست نتایج' },
  'graph.search.list.close': { en: 'Hide match list', fa: 'پنهان کردن فهرست' },
  'graph.toolbar.show-all': { en: 'Show all', fa: 'نمایش همه' },
  'graph.toolbar.show-top-hubs': { en: 'Top hubs', fa: 'گره‌های مرکزی' },
  'graph.toolbar.show-all.hint': {
    en: 'Show every entity, including the long tail',
    fa: 'نمایش همه موجودیت‌ها، شامل دنباله‌ی بلند',
  },
  'graph.toolbar.show-top-hubs.hint': {
    en: 'Hide low-degree nodes; show only the top hubs',
    fa: 'پنهان کردن گره‌های کم‌اتصال؛ فقط گره‌های اصلی',
  },
  'graph.a11y.connections': {
    en: 'Graph connections',
    fa: 'پیوندهای گراف',
  },
  'graph.filters.confidence': {
    en: 'Confidence',
    fa: 'سطح اطمینان',
  },
  'graph.filters.confidence.all': { en: 'All', fa: 'همه' },
  'graph.filters.confidence.mid': { en: 'Medium+', fa: 'متوسط به بالا' },
  'graph.filters.confidence.high': { en: 'High', fa: 'بالا' },
  'graph.filters.density': { en: 'Density', fa: 'تراکم' },
  'graph.filters.density.hubs-only': {
    en: 'Hubs only',
    fa: 'فقط گره‌های مرکزی',
  },
  'graph.filters.community': { en: 'Communities', fa: 'انجمن‌ها' },
  'graph.filters.community.show-all': { en: 'Show all', fa: 'نمایش همه' },
  'graph.filters.community.entities': { en: 'entities', fa: 'موجودیت' },
  'graph.filters.sources': { en: 'Source documents', fa: 'اسناد منبع' },
  'graph.filters.year': { en: 'Year', fa: 'سال' },
  'graph.filters.year.from': { en: 'From', fa: 'از' },
  'graph.filters.year.to': { en: 'To', fa: 'تا' },
  'graph.filters.year.any': { en: 'Any', fa: 'هرچه' },
  'graph.filters.year.reset': { en: 'Reset', fa: 'بازنشانی' },
  'graph.context.menu': { en: 'Node actions', fa: 'اقدامات گره' },
  'graph.pinned.label': { en: 'Pinned', fa: 'سنجاق‌شده' },
  'graph.mode.overview': { en: 'Overview', fa: 'نمای کلی' },
  'graph.mode.focus': { en: 'Focus', fa: 'تمرکز' },
  'graph.mode.path': { en: 'Path', fa: 'مسیر' },
  'graph.input.entity-name': { en: 'Entity name', fa: 'نام موجودیت' },
  'graph.input.path-from': { en: 'From', fa: 'از' },
  'graph.input.path-to': { en: 'To', fa: 'به' },
  'common.reset': { en: 'Reset', fa: 'بازنشانی' },
  'splitPane.open': { en: 'Open pane', fa: 'باز کردن صفحه' },
  'splitPane.close': { en: 'Collapse pane', fa: 'بستن صفحه' },
  'splitPane.resize': { en: 'Resize pane', fa: 'تغییر اندازه صفحه' },
  'pane.status.title': { en: 'Status', fa: 'وضعیت' },
  'pane.stats.title': { en: 'Stats', fa: 'آمار' },
  'pane.status.classification': { en: 'classification', fa: 'طبقه‌بندی' },
  'pane.status.classification.value': { en: 'RESTRICTED', fa: 'محدودشده' },
  'pane.status.clearance': { en: 'clearance', fa: 'دسترسی' },
  'pane.status.session': { en: 'session', fa: 'نشست' },
  'pane.status.session.live': { en: 'LIVE', fa: 'فعال' },
  'pane.status.signed-in': { en: 'signed in as', fa: 'ورود به‌عنوان' },
  'pane.status.org': { en: 'active organization', fa: 'سازمان فعال' },
  'pane.stats.empty': {
    en: 'Graph stats unavailable.',
    fa: 'آمار گراف در دسترس نیست.',
  },
  'source-badge.open': {
    en: 'Open source {id}',
    fa: 'باز کردن منبع {id}',
  },
  'source-badge.lang.fa': {
    en: 'Persian source',
    fa: 'منبع فارسی',
  },
  'source-badge.lang.en': {
    en: 'English source',
    fa: 'منبع انگلیسی',
  },
  'source-badge.retrieval.semantic.aria': {
    en: 'Retrieved via semantic path',
    fa: 'بازیابی‌شده از مسیر معنایی',
  },
  'source-badge.retrieval.hybrid.aria': {
    en: 'Retrieved via hybrid path',
    fa: 'بازیابی‌شده از مسیر ترکیبی',
  },
  'source-badge.retrieval.semantic.title': {
    en: 'Found by semantic vector search only',
    fa: 'تنها از طریق جست‌وجوی برداری معنایی یافت شد',
  },
  'source-badge.retrieval.hybrid.title': {
    en: 'Found by both graph and semantic retrieval',
    fa: 'هم از طریق گراف و هم بازیابی معنایی یافت شد',
  },
  'source-badge.capture': {
    en: 'Save to investigation',
    fa: 'ذخیره در تحقیق',
  },
  'source-badge.freshness.this-year': {
    en: 'this year',
    fa: 'امسال',
  },
  'source-badge.freshness.year-ago': {
    en: '1 year ago',
    fa: 'یک سال پیش',
  },
  'source-badge.freshness.years-ago': {
    en: '{n} years ago',
    fa: '{n} سال پیش',
  },
  'graph.insight.remove-parsi': {
    en: 'Remove Parsi from the graph',
    fa: 'حذف پارسی از گراف',
  },
  'graph.insight.restore-parsi': {
    en: 'Restore Parsi',
    fa: 'بازگرداندن پارسی',
  },
  'graph.insight.components': {
    en: '{n} connected components',
    fa: '{n} مؤلفه متصل',
  },
  'graph.insight.betweenness': {
    en: 'Betweenness top 5',
    fa: 'بالاترین میانجیگری',
  },
  'preset.title': {
    en: 'Try the demo arc',
    fa: 'مسیرهای پیشنهادی دمو',
  },
  'preset.hero.1': {
    en: 'Trace every connection between the IRGC and Robert Malley.',
    fa: 'تمام ارتباطات میان سپاه پاسداران و رابرت مالی را ترسیم کنید.',
  },
  'preset.insight.1': {
    en: 'Who are the main individuals connected to both NIAC and the Quincy Institute?',
    fa: 'افراد اصلی مرتبط با هر دو سازمان نایاک و مؤسسه کوئینسی چه کسانی هستند؟',
  },
  'preset.dossier.1': {
    en: 'Brief me on Trita Parsi.',
    fa: 'درباره تریتا پارسی برای من خلاصه‌ای ارائه دهید.',
  },
  'preset.path.1': {
    en: 'Show the full chain from Open Society Foundations to the US State Department.',
    fa: 'زنجیره کامل از بنیاد جامعه باز تا وزارت خارجه ایالات متحده را نشان دهید.',
  },
  'preset.stumper.1': {
    en: "What is Alavi Foundation's 2024 annual budget?",
    fa: 'بودجه سالانه ۲۰۲۴ بنیاد علوی چقدر است؟',
  },
  'preset.stumper.2': {
    en: "Which Iranian defectors have shared evidence about Siamak Namazi's detention?",
    fa: 'کدام پناهندگان ایرانی درباره بازداشت سیامک نمازی شواهدی ارائه کرده‌اند؟',
  },
  'nav.more': { en: 'More', fa: 'بیشتر' },
  // Capital-flows page
  'capital.hero': {
    en: 'Follow the capital, read the intent.',
    fa: 'سرمایه را دنبال کن، نیت را بخوان.',
  },
  'capital.header-note': {
    en: '{total}M traced · {count} grant records · 2013–2021',
    fa: '{total} میلیون دلار ردیابی‌شده · {count} رکورد کمک · ۲۰۱۳–۲۰۲۱',
  },
  'capital.grants.ranked': {
    en: 'Grant flows, ranked by magnitude',
    fa: 'جریان‌های کمک، بر اساس حجم',
  },
  'capital.bar-note': {
    en: 'bar width = relative amount',
    fa: 'پهنای نوار = مقدار نسبی',
  },
  'capital.pattern.alavi.title': {
    en: 'Flagged — Alavi Foundation',
    fa: 'هشدار — بنیاد علوی',
  },
  'capital.pattern.alavi.body': {
    en: 'Subject of 2008 DOJ civil-forfeiture action alleging the foundation operated as a front for Bank Melli Iran, a sanctioned entity. $500M in assets forfeited 2017.',
    fa: 'موضوع اقدام ضبط مدنی وزارت دادگستری آمریکا در ۲۰۰۸، با این ادعا که بنیاد به‌عنوان پوشش بانک ملی ایران (نهاد تحریم‌شده) عمل می‌کرده است. در ۲۰۱۷ دارایی‌هایی به ارزش ۵۰۰ میلیون دلار ضبط شد.',
  },
  'capital.pattern.ploughshares.title': {
    en: 'Pattern — Ploughshares hub',
    fa: 'الگو — هاب پلاوشِرز',
  },
  'capital.pattern.ploughshares.body': {
    en: 'Ploughshares Fund functions as the structural hub of US arms-control advocacy, distributing the majority of grants in this slice across 40+ recipients.',
    fa: 'صندوق پلاوشِرز به‌عنوان هاب ساختاری جنبش کنترل تسلیحات آمریکا عمل می‌کند و بخش عمده‌ی کمک‌های این برش را میان بیش از ۴۰ گیرنده توزیع می‌کند.',
  },
  // Data-sources page
  'sources.hero': {
    en: 'The cup draws from many wells.',
    fa: 'جام از چاه‌های بسیار می‌نوشد.',
  },
  'sources.header-note': {
    en: '10 federated sources · 7 live, 2 on-demand, 1 static',
    fa: '۱۰ منبع فدرال · ۷ زنده، ۲ بر حسب تقاضا، ۱ ایستا',
  },
  'sources.col.source': { en: 'Source', fa: 'منبع' },
  'sources.col.category': { en: 'Category', fa: 'دسته‌بندی' },
  'sources.col.records': { en: 'Records', fa: 'سوابق' },
  'sources.col.freshness': { en: 'Freshness', fa: 'تازگی' },
  'sources.col.use': { en: 'Primary use', fa: 'کاربرد اصلی' },
  'sources.col.status': { en: 'Status', fa: 'وضعیت' },
  'sources.status.healthy.tooltip': {
    en: 'Source is responding within SLA. Last ingest succeeded; no rate-limit or access errors.',
    fa: 'منبع در محدودهٔ SLA پاسخگو است. آخرین برداشت موفق بود؛ بدون خطای محدودیت یا دسترسی.',
  },
  'sources.status.warn.tooltip': {
    en: 'Degraded — ingest still works but with limits or partial coverage. See note for details.',
    fa: 'افت کیفیت — برداشت ادامه دارد اما با محدودیت یا پوشش جزئی. برای جزئیات یادداشت را ببینید.',
  },
  'sources.status.critical.tooltip': {
    en: 'Source is failing — recent ingests errored or are blocked. See note for details.',
    fa: 'منبع با شکست مواجه است — برداشت‌های اخیر خطا داده یا مسدود شده‌اند. برای جزئیات یادداشت را ببینید.',
  },
  'sources.ontology-title': {
    en: 'Ontology notes',
    fa: 'یادداشت‌های هستی‌شناسی',
  },
  'sources.ontology.body-pre': {
    en: 'Jam-e Jam does not merge records. It maintains source-of-origin for every claim and exposes lineage in every answer. Entity resolution is probabilistic — see the ',
    fa: 'جامِ جم سوابق را ادغام نمی‌کند. منبع اصلی هر ادعا را حفظ کرده و منشأ را در هر پاسخ نشان می‌دهد. انطباق موجودیت احتمالاتی است — ',
  },
  'sources.ontology.body-link': {
    en: 'resolution queue',
    fa: 'صف انطباق',
  },
  'sources.ontology.body-post': {
    en: ' for candidates awaiting analyst promotion.',
    fa: ' را برای گزینه‌های در انتظار تأیید تحلیلگر ببینید.',
  },
  // Ask the Cup query page
  'query.hero': {
    en: 'Ask, and the cup answers.',
    fa: 'بپرس، و جام پاسخ می‌دهد.',
  },
  'query.header-note': {
    en: 'natural-language query · routed across 7 data sources · answered via graph + LLM',
    fa: 'پرسش زبان طبیعی · توزیع‌شده روی ۷ منبع داده · پاسخ از طریق گراف و مدل زبانی',
  },
  'query.console-title': { en: 'Query console', fa: 'کنسول پرسش' },
  'query.placeholder': {
    en: 'Which orgs connect Trita Parsi to Iranian state capital?',
    fa: 'کدام سازمان‌ها تریتا پارسی را به سرمایه نزدیک به دولت ایران متصل می‌کنند؟',
  },
  'query.execute': { en: 'Execute', fa: 'اجرا' },
  'query.try': { en: 'Try', fa: 'پیشنهادها' },
  'query.sources-label': { en: 'Sources', fa: 'منابع' },
  'query.suggest.1': {
    en: 'Which orgs connect Trita Parsi to Iranian state capital?',
    fa: 'کدام سازمان‌ها تریتا پارسی را به سرمایه نزدیک به دولت ایران متصل می‌کنند؟',
  },
  'query.suggest.2': {
    en: 'Map the full downstream grant network of Alavi Foundation since 2010.',
    fa: 'شبکه‌ی کامل کمک‌های بنیاد علوی از سال ۲۰۱۰ را ترسیم کن.',
  },
  'query.suggest.3': {
    en: 'Show me diaspora board members who never appear in FARA filings.',
    fa: 'اعضای هیئت‌مدیره‌ی دیاسپورا را که هرگز در پرونده‌های فارا ظاهر نمی‌شوند نشان بده.',
  },
  // Ask the Cup
  'ask.hero-prompt': {
    en: 'What are you looking for?',
    fa: 'دنبال چه هستید؟',
  },
  'ask.mode.ask': { en: 'Ask', fa: 'پرسش' },
  'ask.mode.template': { en: 'Template', fa: 'قالب' },
  'ask.mode.cypher': { en: 'Cypher', fa: 'سایفر' },
  'ask.mode.entity': { en: 'Entity', fa: 'موجودیت' },
  'ask.submit': { en: 'Ask', fa: 'پرسش' },
  // Signals feed + watchlist
  'signals.empty': {
    en: 'No signals yet. Add entities to your watchlist — the nightly scan will surface changes here.',
    fa: 'هنوز سیگنالی نیست. موجودیت‌ها را به لیست مراقبت اضافه کنید — اسکن شبانه تغییرات را اینجا نشان می‌دهد.',
  },
  'signals.dismiss': { en: 'Dismiss', fa: 'رد کردن' },
  'watchlist.title': { en: 'Watchlist', fa: 'لیست مراقبت' },
  'watchlist.empty': {
    en: 'No entities watched yet.',
    fa: 'هنوز موجودیتی اضافه نشده است.',
  },
  'watchlist.signal-singular': { en: 'signal', fa: 'سیگنال' },
  'watchlist.signal-plural': { en: 'signals', fa: 'سیگنال' },
  'watchlist.add': { en: '+ Add', fa: '+ افزودن' },
  'watchlist.remove': { en: 'Remove', fa: 'حذف' },
  'watchlist.edit': { en: 'Edit', fa: 'ویرایش' },
  'watchlist.edit.description': {
    en: 'Categorize, pin, mute, or annotate this entity.',
    fa: 'این موجودیت را دسته‌بندی، سنجاق، بی‌صدا یا یادداشت‌گذاری کنید.',
  },
  'watchlist.uncategorized': { en: 'Uncategorized', fa: 'بدون دسته' },
  'watchlist.group.pinned': { en: 'Pinned', fa: 'سنجاق‌شده' },
  'watchlist.field.category': { en: 'Category', fa: 'دسته‌بندی' },
  'watchlist.field.category.placeholder': {
    en: 'e.g. lobby, officials, research',
    fa: 'مثال: لابی، مقام‌ها، پژوهش',
  },
  'watchlist.field.note': { en: 'Note', fa: 'یادداشت' },
  'watchlist.field.note.placeholder': {
    en: 'Short context — what to look for.',
    fa: 'بافت کوتاه — به دنبال چه چیزی هستید.',
  },
  'watchlist.field.pinned': { en: 'Pin to top', fa: 'سنجاق به بالا' },
  'watchlist.field.mute': { en: 'Mute signals', fa: 'بی‌صدا کردن سیگنال‌ها' },
  'watchlist.mute.off': { en: 'Off', fa: 'خاموش' },
  'watchlist.mute.1h': { en: 'For 1 hour', fa: 'برای ۱ ساعت' },
  'watchlist.mute.1d': { en: 'For 1 day', fa: 'برای ۱ روز' },
  'watchlist.mute.1w': { en: 'For 1 week', fa: 'برای ۱ هفته' },
  'watchlist.mute.forever': { en: 'Until I unmute', fa: 'تا زمان لغو' },
  'common.save': { en: 'Save', fa: 'ذخیره' },
  'watchlist.since-prefix': {
    en: 'Since your last visit',
    fa: 'از آخرین بازدید',
  },
  'watchlist.since-first-visit': {
    en: 'Welcome back — first morning brief.',
    fa: 'خوش آمدید — اولین گزارش صبحگاهی.',
  },
  // Capture-to-investigation dialog (Phase 2 bridge).
  // Opened from a source row (dossier, ask-result) to log the source as
  // a link in an investigation's evidence chain.
  'capture.triggerLabel': {
    en: 'Save to investigation',
    fa: 'ذخیره در تحقیق',
  },
  'capture.dialogTitle': {
    en: 'Save finding to investigation',
    fa: 'ذخیرهٔ یافته در تحقیق',
  },
  'capture.dialogDescription': {
    en: 'Log this source as a link in an investigation evidence chain.',
    fa: 'این منبع را به‌عنوان یک گره در زنجیرهٔ شواهد یک تحقیق ثبت کنید.',
  },
  'capture.fromDossier': {
    en: 'from {name} dossier',
    fa: 'از پروندهٔ {name}',
  },
  'capture.pickInvestigation': {
    en: 'Pick investigation',
    fa: 'انتخاب تحقیق',
  },
  'capture.newInvestigation': {
    en: 'New investigation…',
    fa: 'تحقیق جدید…',
  },
  'capture.newInvestigationTitleLabel': {
    en: 'Investigation title',
    fa: 'عنوان تحقیق',
  },
  'capture.newInvestigationHypothesisLabel': {
    en: 'Hypothesis (optional)',
    fa: 'فرضیه (اختیاری)',
  },
  'capture.claimLabel': { en: 'Claim', fa: 'ادعا' },
  'capture.claimPlaceholder': {
    en: 'What does this source support?',
    fa: 'این منبع از چه چیزی پشتیبانی می‌کند؟',
  },
  'capture.confidenceLabel': { en: 'Confidence', fa: 'اطمینان' },
  'capture.confidenceLow': { en: 'low', fa: 'پایین' },
  'capture.confidenceMedium': { en: 'medium', fa: 'متوسط' },
  'capture.confidenceHigh': { en: 'high', fa: 'بالا' },
  'capture.saveCta': {
    en: 'Save to investigation',
    fa: 'ذخیره در تحقیق',
  },
  'capture.saving': { en: 'Saving…', fa: '…در حال ذخیره' },
  'capture.cancelCta': { en: 'Cancel', fa: 'لغو' },
  'capture.savedToastPrefix': {
    en: 'Saved to “',
    fa: 'ذخیره شد در «',
  },
  'capture.viewCta': {
    en: 'View investigation',
    fa: 'مشاهدهٔ تحقیق',
  },
  'capture.addAnother': {
    en: 'Add another',
    fa: 'افزودن مدرک دیگر',
  },
  'capture.closeCta': { en: 'Close', fa: 'بستن' },
  'capture.missingClaim': {
    en: 'Enter a claim before saving.',
    fa: 'متن ادعا را وارد کنید.',
  },
  'capture.missingPick': {
    en: 'Pick an investigation first.',
    fa: 'یک تحقیق را انتخاب کنید.',
  },
  'capture.loadError': {
    en: 'Failed to load investigations.',
    fa: 'خطا در بارگیری تحقیقات.',
  },
  'capture.emptyInvestigations': {
    en: 'No investigations yet — create a new one.',
    fa: 'هنوز تحقیقی ایجاد نکرده‌اید.',
  },
  // Investigation → brief composer handoff CTA.
  'investigationBrief.composeCta': {
    en: 'Compose brief from investigation',
    fa: 'ساخت گزارش از این تحقیق',
  },
  'investigationBrief.composeAria': {
    en: 'Compose brief from this investigation',
    fa: 'ساخت گزارش از این تحقیق',
  },
  // Palette slash-commands + verb dispatches (Phase 2 additions).
  'palette.cmd.newInvestigation': {
    en: 'Create investigation',
    fa: 'ایجاد تحقیق جدید',
  },
  'palette.cmd.viewBriefs': {
    en: 'View my briefs',
    fa: 'مشاهدهٔ گزارش‌ها',
  },
  'palette.verb.investigate': {
    en: 'Investigate {target}',
    fa: 'تحقیق دربارهٔ {target}',
  },
  // Public (principal-facing) shared brief — unauthenticated /b/$token page.
  'publicBrief.brandLabel': {
    en: 'Shared brief',
    fa: 'سند به‌اشتراک‌گذاشته‌شده',
  },
  'publicBrief.preparedBy': {
    en: 'Prepared by {name}',
    fa: 'تهیه‌شده توسط {name}',
  },
  'publicBrief.preparedAt': {
    en: 'Prepared {date}',
    fa: 'تاریخ تهیه: {date}',
  },
  'publicBrief.sharedAt': {
    en: 'Prepared for shared access · {date}',
    fa: 'برای دسترسی مشترک آماده شده · {date}',
  },
  'publicBrief.limitedEvidence': {
    en: 'Limited evidence — review before citing.',
    fa: 'شواهد محدود — پیش از استناد بازبینی شود.',
  },
  'publicBrief.notAvailableTitle': {
    en: 'This shared brief is no longer available.',
    fa: 'این سند دیگر در دسترس نیست.',
  },
  'publicBrief.notAvailableBody': {
    en: 'The analyst may have revoked access. Contact them for a fresh link.',
    fa: 'ممکن است تحلیلگر دسترسی را لغو کرده باشد. برای لینک تازه با او تماس بگیرید.',
  },
  'publicBrief.networkError': {
    en: 'Unable to load this brief. Try again in a moment.',
    fa: 'بارگذاری ممکن نشد. لحظه‌ای دیگر دوباره تلاش کنید.',
  },
  'publicBrief.loading': {
    en: 'Loading brief…',
    fa: '…در حال بارگذاری',
  },
  'publicBrief.footerTagline': {
    en: 'Built for principals who cannot afford a fabricated citation.',
    fa: 'برای کسانی ساخته شده که استنادِ جعلی را برنمی‌تابند.',
  },
  'publicBrief.executiveSummary': {
    en: 'Executive summary',
    fa: 'خلاصه اجرایی',
  },
  'publicBrief.analysis': {
    en: 'Analysis',
    fa: 'تحلیل',
  },
  'publicBrief.sources': {
    en: 'Sources',
    fa: 'منابع',
  },
  'publicBrief.sourcesCount': {
    en: '{n} sources',
    fa: '{n} منبع',
  },
  // Admin settings hub — /admin/settings. Admin-only nav + page copy.
  'menu.admin-settings': { en: 'Settings', fa: 'تنظیمات' },
  'admin.header': { en: 'Admin settings', fa: 'تنظیمات مدیر' },
  'admin.subtitle': {
    en: 'Platform-wide configuration. Changes take effect immediately for all analysts.',
    fa: 'پیکربندی سراسری. تغییرات برای همه تحلیل‌گران بلافاصله اعمال می‌شود.',
  },
  'admin.signed-in-as': { en: 'Signed in as', fa: 'ورود با' },
  'admin.section.graph': { en: 'Graph & grouping', fa: 'شبکه و خوشه‌بندی' },
  'admin.section.import': { en: 'Data import', fa: 'ورود داده' },
  'admin.section.system': { en: 'System health', fa: 'سلامت سیستم' },
  'admin.section.graph.desc': {
    en: 'Control how the network overview groups entities into hub-islands.',
    fa: 'نحوه خوشه‌بندی موجودیت‌ها در نمای کلی شبکه را تنظیم کنید.',
  },
  'admin.section.import.desc': {
    en: 'Ingest corpora into the knowledge graph.',
    fa: 'واردکردن مجموعه داده به گراف دانش.',
  },
  'admin.section.system.desc': {
    en: 'Neo4j connection health and live probes.',
    fa: 'سلامت اتصال نئو۴جی و بررسی‌های زنده.',
  },
  'admin.section.jobs': { en: 'Background jobs', fa: 'کارهای پس‌زمینه' },
  'admin.section.jobs.desc': {
    en: 'Cron schedules, last-run summary, and manual triggers for the streamliner, signal refresh, and audit redaction jobs.',
    fa: 'زمان‌بندی کرون، آخرین اجرا و راه‌اندازی دستی برای استریم‌لاینر، به‌روزرسانی سیگنال و سانسور ممیزی.',
  },
  'admin.jobs.scheduler-dormant': {
    en: 'Cloud Scheduler API is disabled on this project — cron entries are wired but not firing. Use "Run now" for manual triggers.',
    fa: 'Cloud Scheduler API روی این پروژه غیرفعال است — کرون‌ها سیم‌کشی شده‌اند ولی اجرا نمی‌شوند. از «اجرای آنی» استفاده کنید.',
  },
  'admin.jobs.run-now': { en: 'Run now', fa: 'اجرای آنی' },
  'admin.jobs.running': { en: 'Running…', fa: 'در حال اجرا…' },
  'admin.jobs.last-run': { en: 'Last run', fa: 'آخرین اجرا' },
  'admin.jobs.last-outcome': { en: 'Outcome', fa: 'نتیجه' },
  'admin.jobs.last-detail': { en: 'Detail', fa: 'جزئیات' },
  'admin.jobs.never-run': { en: 'Never run', fa: 'هرگز اجرا نشده' },
  'admin.jobs.outcome.ok': { en: 'OK', fa: 'موفق' },
  'admin.jobs.outcome.warn': { en: 'Warnings', fa: 'هشدارها' },
  'admin.jobs.outcome.failed': { en: 'Failed', fa: 'ناموفق' },
  'admin.jobs.outcome.unknown': { en: 'Unknown', fa: 'نامشخص' },
  'admin.jobs.edit-note': {
    en: 'Schedule edits require redeploy: bash scripts/deploy.sh',
    fa: 'تغییر زمان‌بندی نیازمند استقرار مجدد است: bash scripts/deploy.sh',
  },
  'admin.jobs.signalRefresh.title': {
    en: 'Watchlist signal refresh',
    fa: 'به‌روزرسانی سیگنال فهرست پیگیری',
  },
  'admin.jobs.signalRefresh.desc': {
    en: 'Diffs every org’s watchlist against the graph and emits Signal entries for changes since the last run.',
    fa: 'فهرست پیگیری هر سازمان را با گراف مقایسه می‌کند و رویدادها را از آخرین اجرا منتشر می‌کند.',
  },
  'admin.jobs.auditRedact.title': {
    en: 'Audit PII redaction',
    fa: 'سانسور اطلاعات شخصی ممیزی',
  },
  'admin.jobs.auditRedact.desc': {
    en: 'Strips IP-hash, country, and user-agent fields off audit entries past their redactAfter horizon.',
    fa: 'حذف IP-hash و کشور و user-agent از ورودی‌های ممیزی پس از پایان دورهٔ حفظ.',
  },
  'admin.jobs.streamliner.title': {
    en: 'Streamliner agent',
    fa: 'عامل استریم‌لاینر',
  },
  'admin.jobs.streamliner.desc': {
    en: 'Graph hygiene pass: alias / low-confidence-fact / missing-edge / redundant-schema candidates per org.',
    fa: 'پاک‌سازی گراف: گزینه‌های هم‌نام / واقعه کم‌اطمینان / یال گمشده / طرح‌وارهٔ تکراری برای هر سازمان.',
  },
  'admin.graph.hub-count': { en: 'Hubs on overview', fa: 'تعداد مرکزهای نمای کلی' },
  'admin.graph.hub-count.help': {
    en: 'Top N most-connected entities to show as island anchors on the overview.',
    fa: 'N موجودیت با بیشترین ارتباط به‌عنوان مرکز جزیره‌ها در نمای کلی نمایش داده می‌شود.',
  },
  'admin.graph.depth': { en: 'Neighborhood depth', fa: 'عمق همسایگی' },
  'admin.graph.depth.help': {
    en: 'How many hops from each hub to include. Depth 2 is denser but can bridge into other hubs.',
    fa: 'چند گام از هر مرکز شامل شود. عمق ۲ متراکم‌تر است اما ممکن است با مراکز دیگر هم‌پوشانی کند.',
  },
  'admin.graph.depth.1': { en: '1 hop (direct)', fa: '۱ گام (مستقیم)' },
  'admin.graph.depth.2': { en: '2 hops (denser)', fa: '۲ گام (متراکم‌تر)' },
  'admin.graph.selection': { en: 'Hub selection', fa: 'روش انتخاب مرکز' },
  'admin.graph.selection.help': {
    en: 'Only degree centrality is active today. Other strategies are planned.',
    fa: 'امروز فقط مرکزیت درجه فعال است. سایر راهبردها در برنامه هستند.',
  },
  'admin.graph.selection.degree': { en: 'Degree centrality', fa: 'مرکزیت درجه' },
  'admin.graph.selection.betweenness': {
    en: 'Betweenness (coming soon)',
    fa: 'میانجیگری (به‌زودی)',
  },
  'admin.graph.selection.curated': {
    en: 'Curated list (coming soon)',
    fa: 'فهرست دستی (به‌زودی)',
  },
  'admin.graph.save': { en: 'Save configuration', fa: 'ذخیرهٔ تنظیمات' },
  'admin.graph.saving': { en: 'Saving…', fa: '…در حال ذخیره' },
  'admin.graph.saved': { en: 'Saved', fa: 'ذخیره شد' },
  'admin.graph.save-error': {
    en: "Couldn't save configuration. Try again.",
    fa: 'ذخیرهٔ تنظیمات ممکن نشد. دوباره تلاش کنید.',
  },
  'admin.graph.last-updated': {
    en: 'Last updated by {email} at {date}',
    fa: 'آخرین به‌روزرسانی توسط {email} در {date}',
  },
  'admin.graph.load-error': {
    en: "Couldn't load current configuration.",
    fa: 'بارگیری تنظیمات فعلی ممکن نشد.',
  },
  'admin.import.format-title': { en: 'Corpus format', fa: 'قالب مجموعه داده' },
  'admin.import.upload-title': { en: 'Upload corpus', fa: 'بارگذاری مجموعه' },
  'admin.import.upload-help': {
    en: 'Pick a .json file to preview. Backend ingest endpoint is not wired yet — for now run `pnpm ingest` locally against the file you selected.',
    fa: 'یک فایل .json را برای پیش‌نمایش انتخاب کنید. نقطه پایانی ورود داده هنوز وصل نشده — فعلاً به‌صورت محلی `pnpm ingest` را روی فایل انتخابی اجرا کنید.',
  },
  'admin.import.selected': {
    en: 'Selected: {name} ({size})',
    fa: 'انتخاب‌شده: {name} ({size})',
  },
  'admin.import.pick-file': { en: 'Pick .json file', fa: 'انتخاب فایل .json' },
  'admin.import.import-disabled': {
    en: 'Import (coming soon)',
    fa: 'ورود داده (به‌زودی)',
  },
  'admin.import.sources-cta': {
    en: 'Browse planned data sources',
    fa: 'مرور منابع داده برنامه‌ریزی‌شده',
  },
  'admin.import.corpus-stats': {
    en: 'Current corpus',
    fa: 'مجموعهٔ فعلی',
  },
  'admin.import.stats.entities': { en: 'Entities', fa: 'موجودیت‌ها' },
  'admin.import.stats.relationships': { en: 'Relationships', fa: 'روابط' },
  'admin.import.stats.documents': { en: 'Documents', fa: 'اسناد' },
  'admin.import.stats.top-label': { en: 'Top label', fa: 'برچسب برتر' },
  // Dedup: this file was imported before
  'admin.import.prior-runs.badge': { en: 'SEEN', fa: 'قبلاً دیده‌شده' },
  'admin.import.prior-runs.title': {
    en: 'This file has been imported before',
    fa: 'این فایل قبلاً وارد شده است',
  },
  'admin.import.prior-runs.explain': {
    en: 'Re-running is safe — entities are upserted by name, not duplicated.',
    fa: 'اجرای مجدد ایمن است — موجودیت‌ها بر اساس نام به‌روزرسانی می‌شوند، نه تکرار.',
  },
  // Structured run-start errors
  'admin.import.error.already-running.title': {
    en: 'Another ingest is already running',
    fa: 'یک ورود داده در حال اجراست',
  },
  'admin.import.error.already-running.subtitle': {
    en: 'Started by {who} at {when}. Wait for it to finish, then try again.',
    fa: 'توسط {who} در {when} شروع شده است. منتظر پایان آن بمانید، سپس دوباره تلاش کنید.',
  },
  'admin.import.error.payload-too-large.title': {
    en: 'File too large',
    fa: 'حجم فایل بیش از حد مجاز است',
  },
  'admin.import.error.payload-too-large.subtitle': {
    en: 'The server rejected this payload. Max {maxMb} MB.',
    fa: 'سرور این محموله را رد کرد. حداکثر {maxMb} مگابایت.',
  },
  // Diff preview (validated state)
  'admin.import.diff.title': {
    en: 'What this run will change',
    fa: 'این اجرا چه چیزی را تغییر می‌دهد',
  },
  'admin.import.diff.loading': {
    en: 'Comparing against the current graph…',
    fa: '…در حال مقایسه با گراف فعلی',
  },
  'admin.import.diff.unavailable': {
    en: 'Couldn’t reach the graph to compute a diff — re-run will still upsert correctly.',
    fa: 'دسترسی به گراف برای مقایسه ممکن نبود — اجرای مجدد همچنان به‌درستی به‌روزرسانی می‌کند.',
  },
  'admin.import.diff.new-entities': { en: 'New entities', fa: 'موجودیت‌های جدید' },
  'admin.import.diff.existing-entities': {
    en: 'Existing (will upsert)',
    fa: 'موجود (به‌روزرسانی می‌شوند)',
  },
  'admin.import.diff.new-edges': { en: 'New edges', fa: 'یال‌های جدید' },
  'admin.import.diff.existing-edges': {
    en: 'Existing edges',
    fa: 'یال‌های موجود',
  },
  'admin.import.diff.sample-new': {
    en: 'Sample new entities',
    fa: 'نمونهٔ موجودیت‌های جدید',
  },
  'admin.import.diff.sample-existing': {
    en: 'Sample existing entities',
    fa: 'نمونهٔ موجودیت‌های موجود',
  },
  // Cancel a running ingest
  'admin.import.run.cancel': { en: 'Cancel run', fa: 'لغو اجرا' },
  'admin.import.run.confirm-cancel': {
    en: 'Confirm cancel?',
    fa: 'لغو تأیید شود؟',
  },
  'admin.import.run.canceling': { en: 'Canceling…', fa: '…در حال لغو' },
  'admin.import.run.canceled': {
    en: 'Ingest canceled',
    fa: 'ورود داده لغو شد',
  },
  // Rollback (recent-runs row action)
  'admin.import.rollback.start': { en: 'Roll back', fa: 'بازگشت' },
  'admin.import.rollback.confirm': {
    en: 'Confirm rollback?',
    fa: 'بازگشت تأیید شود؟',
  },
  'admin.import.rollback.pending': {
    en: 'Rolling back…',
    fa: '…در حال بازگشت',
  },
  'admin.import.rollback.help': {
    en: 'Deletes every node and edge stamped with this run’s id.',
    fa: 'هر گره و یالی که با شناسهٔ این اجرا علامت‌گذاری شده حذف می‌شود.',
  },
  'admin.import.rollback.success': {
    en: 'Removed {nodes} nodes and {edges} edges.',
    fa: '{nodes} گره و {edges} یال حذف شد.',
  },
  'admin.import.rollback.still-running': {
    en: 'Cancel the run before rolling it back.',
    fa: 'برای بازگشت، ابتدا اجرا را لغو کنید.',
  },
  'admin.import.rollback.badge': { en: 'ROLLED BACK', fa: 'بازگشت‌شده' },
  'admin.import.rollback.stamped': {
    en: 'Rolled back {when} by {who} — removed {nodes} nodes and {edges} edges.',
    fa: 'در {when} توسط {who} بازگشت داده شد — {nodes} گره و {edges} یال حذف شد.',
  },
  // Drop zone + parsing
  'admin.import.dropzone.title': {
    en: 'Drop a corpus JSON here',
    fa: 'فایل JSON مجموعه را اینجا رها کنید',
  },
  'admin.import.dropzone.hint': {
    en: 'or click to choose a file • max {maxMb} MB • up to {maxDocs} documents',
    fa: 'یا برای انتخاب فایل کلیک کنید • حداکثر {maxMb} مگابایت • تا {maxDocs} سند',
  },
  'admin.import.dropzone.too-large': {
    en: 'File exceeds {maxMb} MB limit',
    fa: 'حجم فایل بیش از {maxMb} مگابایت است',
  },
  'admin.import.dropzone.bad-type': {
    en: 'Only .json files are accepted',
    fa: 'فقط فایل‌های .json پذیرفته می‌شوند',
  },
  'admin.import.parsing': { en: 'Parsing…', fa: '…در حال تجزیه' },
  'admin.import.parse-error': {
    en: "Couldn't parse this file",
    fa: 'تجزیهٔ این فایل ممکن نشد',
  },
  'admin.import.parse-error.json': {
    en: 'Invalid JSON',
    fa: 'JSON نامعتبر',
  },
  'admin.import.parse-error.schema': {
    en: 'Schema mismatch — expected the corpus shape shown below',
    fa: 'ساختار داده مطابقت ندارد — قالب مورد انتظار در زیر آمده است',
  },
  'admin.import.parse-error.field': {
    en: 'Field {path}: {message}',
    fa: 'فیلد {path}: {message}',
  },
  // Validation preview
  'admin.import.preview.title': {
    en: 'Ready to ingest',
    fa: 'آمادهٔ ورود داده',
  },
  'admin.import.preview.file': { en: 'File', fa: 'فایل' },
  'admin.import.preview.corpus': { en: 'Corpus', fa: 'مجموعه' },
  'admin.import.preview.source': {
    en: 'Source document',
    fa: 'سند مرجع',
  },
  'admin.import.preview.documents': {
    en: 'Documents',
    fa: 'اسناد',
  },
  'admin.import.preview.entities': {
    en: 'Expected entities',
    fa: 'موجودیت‌های مورد انتظار',
  },
  'admin.import.preview.edges': {
    en: 'Expected edges',
    fa: 'یال‌های مورد انتظار',
  },
  'admin.import.preview.relationships': {
    en: 'Expected relationships',
    fa: 'روابط مورد انتظار',
  },
  'admin.import.preview.sample-entities': {
    en: 'Sample entities',
    fa: 'نمونهٔ موجودیت‌ها',
  },
  'admin.import.preview.sample-edges': {
    en: 'Sample edges',
    fa: 'نمونهٔ یال‌ها',
  },
  'admin.import.preview.warnings': {
    en: 'Warnings',
    fa: 'هشدارها',
  },
  'admin.import.preview.show-all-warnings': {
    en: 'Show all {count} warnings',
    fa: 'نمایش همهٔ {count} هشدار',
  },
  // Mode toggle + start
  'admin.import.mode.title': { en: 'Mode', fa: 'حالت' },
  'admin.import.mode.merge': {
    en: 'Merge — additive',
    fa: 'ادغام — افزوده',
  },
  'admin.import.mode.merge.help': {
    en: 'Adds new entities and edges; existing data is preserved.',
    fa: 'موجودیت‌ها و یال‌های جدید افزوده می‌شوند؛ داده‌های موجود حفظ می‌شود.',
  },
  'admin.import.mode.replace': {
    en: 'Replace — wipe + reload',
    fa: 'جایگزینی — حذف و بارگذاری دوباره',
  },
  'admin.import.mode.replace.help': {
    en: 'Deletes the entire graph before ingesting. Cannot be undone.',
    fa: 'پیش از ورود داده، کل گراف حذف می‌شود. این عمل قابل بازگشت نیست.',
  },
  'admin.import.start': { en: 'Start ingest', fa: 'شروع ورود داده' },
  'admin.import.confirm-replace': {
    en: 'Confirm replace?',
    fa: 'تأیید جایگزینی؟',
  },
  'admin.import.cancel': { en: 'Cancel', fa: 'انصراف' },
  // Run progress
  'admin.import.run.phase.constraints': {
    en: 'Constraints',
    fa: 'محدودیت‌ها',
  },
  'admin.import.run.phase.clear': {
    en: 'Clearing graph',
    fa: 'پاک‌سازی گراف',
  },
  'admin.import.run.phase.documents': {
    en: 'Documents',
    fa: 'اسناد',
  },
  'admin.import.run.phase.demo_seeds': {
    en: 'Demo seeds',
    fa: 'دانه‌های نمایشی',
  },
  'admin.import.run.phase.finalize': {
    en: 'Finalizing',
    fa: 'نهایی‌سازی',
  },
  'admin.import.run.docs-processed': {
    en: 'Documents processed',
    fa: 'اسناد پردازش‌شده',
  },
  'admin.import.run.nodes-written': {
    en: 'Nodes written',
    fa: 'گره‌های ثبت‌شده',
  },
  'admin.import.run.edges-written': {
    en: 'Edges written',
    fa: 'یال‌های ثبت‌شده',
  },
  'admin.import.run.errors': { en: 'Errors', fa: 'خطاها' },
  'admin.import.run.log-title': { en: 'Live log', fa: 'گزارش زنده' },
  'admin.import.run.complete': {
    en: 'Ingest completed',
    fa: 'ورود داده تکمیل شد',
  },
  'admin.import.run.partial': {
    en: 'Ingest finished with errors',
    fa: 'ورود داده با خطا پایان یافت',
  },
  'admin.import.run.failed': {
    en: 'Ingest failed',
    fa: 'ورود داده ناموفق بود',
  },
  'admin.import.run.duration': {
    en: 'in {duration}',
    fa: 'در {duration}',
  },
  'admin.import.run.done': { en: 'Done', fa: 'پایان' },
  'admin.import.run.import-another': {
    en: 'Import another',
    fa: 'بارگذاری مجموعهٔ دیگر',
  },
  // Recent runs table
  'admin.import.runs.title': {
    en: 'Recent ingest runs',
    fa: 'اجراهای اخیر ورود داده',
  },
  'admin.import.runs.empty': {
    en: 'No ingest runs yet',
    fa: 'هنوز اجرایی ثبت نشده است',
  },
  'admin.import.runs.refresh': { en: 'Refresh', fa: 'تازه‌سازی' },
  'admin.import.runs.col.started': { en: 'Started', fa: 'شروع' },
  'admin.import.runs.col.by': { en: 'By', fa: 'توسط' },
  'admin.import.runs.col.mode': { en: 'Mode', fa: 'حالت' },
  'admin.import.runs.col.status': { en: 'Status', fa: 'وضعیت' },
  'admin.import.runs.col.docs': { en: 'Documents', fa: 'اسناد' },
  'admin.import.runs.col.nodes': { en: 'Nodes', fa: 'گره‌ها' },
  'admin.import.runs.col.edges': { en: 'Edges', fa: 'یال‌ها' },
  'admin.import.runs.col.errors': { en: 'Errors', fa: 'خطاها' },
  'admin.import.runs.col.duration': { en: 'Duration', fa: 'مدت' },
  'admin.import.runs.expanded.error-log': {
    en: 'Error log',
    fa: 'گزارش خطاها',
  },
  'admin.import.runs.expanded.no-errors': {
    en: 'No errors recorded',
    fa: 'خطایی ثبت نشده است',
  },
  'admin.import.status.queued': { en: 'Queued', fa: 'در صف' },
  'admin.import.status.running': { en: 'Running', fa: 'در حال اجرا' },
  'admin.import.status.completed': {
    en: 'Completed',
    fa: 'تکمیل‌شده',
  },
  'admin.import.status.partial': { en: 'Partial', fa: 'ناقص' },
  'admin.import.status.failed': { en: 'Failed', fa: 'ناموفق' },
  'admin.import.status.canceled': { en: 'Canceled', fa: 'لغو شد' },
  'admin.system.refresh': { en: 'Refresh', fa: 'تازه‌سازی' },
  'admin.system.refreshing': { en: 'Checking…', fa: '…در حال بررسی' },
  'admin.system.status': { en: 'Status', fa: 'وضعیت' },
  'admin.system.healthy': { en: 'Healthy', fa: 'سالم' },
  'admin.system.failed': { en: 'Failed', fa: 'ناموفق' },
  'admin.system.server': { en: 'Server', fa: 'سرور' },
  'admin.system.uri': { en: 'URI', fa: 'آدرس' },
  'admin.system.latency': { en: 'Latency', fa: 'تأخیر' },
  'admin.system.checked-at': { en: 'Checked at', fa: 'زمان بررسی' },
  'admin.system.counts': { en: 'Counts', fa: 'شمارش‌ها' },
  'admin.system.top-labels': { en: 'Top labels', fa: 'برچسب‌های برتر' },
  'admin.system.top-rels': { en: 'Top relationships', fa: 'روابط برتر' },
  'admin.system.load-error': {
    en: "Couldn't load system health.",
    fa: 'بارگیری سلامت سیستم ممکن نشد.',
  },
  // Organization / multi-tenant strings.
  'org.create': { en: 'Create organization', fa: 'ایجاد سازمان' },
  'org.edit': { en: 'Edit organization', fa: 'ویرایش سازمان' },
  'org.members': { en: 'Members', fa: 'اعضا' },
  'org.invites': { en: 'Invites', fa: 'دعوت‌نامه‌ها' },
  'org.settings': { en: 'Organization settings', fa: 'تنظیمات سازمان' },
  'org.ingest.nav': { en: 'Import data', fa: 'ورود داده' },
  'org.ingest.eyebrow': { en: 'organization data', fa: 'داده‌های سازمان' },
  'org.ingest.title': { en: 'Import data', fa: 'ورود داده' },
  'org.ingest.description': {
    en: 'Layer your own corpus on top of system data. Documents, edges, and resolution candidates are tagged with your organization id and visible only to your members alongside the shared system corpus. Active organization:',
    fa: 'مجموعهٔ داده‌های خود را روی داده‌های سیستمی بیفزایید. اسناد، یال‌ها و نامزدهای انطباق با شناسهٔ سازمان شما برچسب می‌خورند و فقط برای اعضای سازمان شما در کنار داده‌های سیستم قابل‌مشاهده‌اند. سازمان فعال:',
  },
  'org.ingest.pick-file': { en: 'Corpus JSON file', fa: 'فایل JSON مجموعهٔ داده' },
  'org.ingest.run': { en: 'Run import', fa: 'اجرای ورود' },
  'org.ingest.running': { en: 'Importing…', fa: 'در حال ورود…' },
  'org.ingest.detach': {
    en: 'Detach (run continues server-side)',
    fa: 'جداسازی (اجرا روی سرور ادامه دارد)',
  },
  'org.ingest.detached.hint': {
    en: 'Stopped listening. The server may still be running this import.',
    fa: 'گوش‌دادن متوقف شد. ممکن است این ورود همچنان روی سرور در حال اجرا باشد.',
  },
  'org.ingest.progress': { en: 'Progress', fa: 'پیشرفت' },
  'org.ingest.docs': { en: 'Documents', fa: 'اسناد' },
  'org.ingest.nodes': { en: 'Nodes', fa: 'گره‌ها' },
  'org.ingest.edges': { en: 'Edges', fa: 'یال‌ها' },
  'org.ingest.errors': { en: 'Errors', fa: 'خطاها' },
  'org.ingest.complete': {
    en: 'Import complete (status: {status}).',
    fa: 'ورود کامل شد (وضعیت: {status}).',
  },
  'org.ingest.failed': { en: 'Import failed.', fa: 'ورود شکست خورد.' },
  'org.ingest.stream-lost.title': {
    en: 'Live stream disconnected',
    fa: 'پخش زنده قطع شد',
  },
  'org.ingest.stream-lost.body': {
    en: 'The server is still ingesting this run. Progress below keeps updating from the server-side run record; the final status will appear here when the run finishes.',
    fa: 'سرور هنوز در حال ورود این اجرا است. پیشرفت زیر از روی رکورد اجرای سمت سرور به‌روزرسانی می‌شود؛ وضعیت نهایی هنگام پایان اجرا اینجا نمایش داده می‌شود.',
  },
  'org.ingest.log.title': { en: 'Live log', fa: 'گزارش زنده' },
  'org.ingest.detail.tab': { en: 'Run detail', fa: 'جزئیات اجرا' },
  'org.ingest.detail.not-found': {
    en: 'Run not found (it may have been deleted, or belongs to a different organization).',
    fa: 'اجرا یافت نشد (ممکن است حذف شده باشد یا متعلق به سازمان دیگری باشد).',
  },
  'org.ingest.detail.status': { en: 'Status', fa: 'وضعیت' },
  'org.ingest.detail.format': { en: 'Format', fa: 'قالب' },
  'org.ingest.detail.mode': { en: 'Mode', fa: 'حالت' },
  'org.ingest.detail.size': { en: 'File size', fa: 'حجم فایل' },
  'org.ingest.detail.started': { en: 'Started', fa: 'شروع' },
  'org.ingest.detail.duration': { en: 'Duration', fa: 'مدت' },
  'org.ingest.detail.actor': { en: 'Started by', fa: 'آغازگر' },
  'org.ingest.detail.error-log': { en: 'Error log', fa: 'گزارش خطاها' },
  'org.ingest.detail.no-errors': {
    en: 'No errors recorded for this run.',
    fa: 'هیچ خطایی برای این اجرا ثبت نشده است.',
  },
  'org.ingest.detail.retry.title': {
    en: 'Re-import the same data',
    fa: 'ورود مجدد همان داده',
  },
  'org.ingest.detail.retry.body': {
    en: 'Submits the same payload the server stored for this run, creating a new entry in the import history. Idempotent — already-ingested entities upsert in place via MERGE-by-name + deterministic doc IDs.',
    fa: 'همان محتوای ذخیره‌شدهٔ این اجرا را دوباره ارسال می‌کند و یک ورودی جدید در تاریخچه می‌سازد. ایدئم‌پوتنت — موجودیت‌های قبلاً واردشده از طریق MERGE-by-name و شناسه‌های قطعی سند به‌روزرسانی می‌شوند.',
  },
  'org.ingest.detail.retry.cta': {
    en: 'Re-import',
    fa: 'ورود مجدد',
  },
  'org.ingest.detail.retry.in-flight': {
    en: 'Submitting…',
    fa: '…در حال ارسال',
  },
  'org.ingest.detail.retry.payload-gone.title': {
    en: 'Original payload unavailable',
    fa: 'محتوای اصلی در دسترس نیست',
  },
  'org.ingest.detail.retry.payload-gone.body': {
    en: 'This run pre-dates server-side payload storage, or the stored bytes have aged out of retention. Re-upload the file via the picker on the import page to start a fresh run with the same data.',
    fa: 'این اجرا قبل از ذخیره‌سازی محتوا روی سرور انجام شده، یا بایت‌های ذخیره‌شده از دوره نگهداری گذشته‌اند. فایل را از طریق انتخابگر در صفحه ورود داده دوباره بارگذاری کنید تا اجرای جدیدی با همان داده شروع شود.',
  },
  'org.ingest.detail.retry.payload-gone.cta': {
    en: '← Back to picker',
    fa: '← بازگشت به انتخابگر',
  },
  'org.ingest.format-label': { en: 'Source format', fa: 'قالب منبع' },
  'org.ingest.format.canonical.label': {
    en: 'Canonical corpus (JSON)',
    fa: 'مجموعهٔ متعارف (JSON)',
  },
  'org.ingest.format.canonical.hint': {
    en: 'Documents pre-extracted into the Jam-e Jam corpus shape (see example below).',
    fa: 'اسناد از پیش به ساختار مجموعهٔ جام‌جم استخراج‌شده‌اند (مثال زیر را ببینید).',
  },
  'org.ingest.format.fec.label': {
    en: 'FEC contributions (JSON)',
    fa: 'کمک‌های FEC (JSON)',
  },
  'org.ingest.format.fec.hint': {
    en: 'Raw FEC contributions JSON ({ contributions: [...] }). Each row → donor → committee + employer edge.',
    fa: 'JSON خام کمک‌های FEC ({ contributions: [...] }). هر سطر → اهداکننده → کمیته + یال کارفرما.',
  },
  'org.ingest.format.fec-identities.label': {
    en: 'FEC identities (JSON, donor-indexed)',
    fa: 'هویت‌های FEC (JSON، نمایه‌شده با اهداکننده)',
  },
  'org.ingest.format.fec-identities.hint': {
    en: 'FEC donor index: { "DONOR NAME": [{ contributions: [...] }, ...], ... }. Each nested contribution becomes one Document.',
    fa: 'نمایهٔ اهداکنندگان FEC: { "نام اهداکننده": [{ contributions: [...] }, ...], ... }. هر کمک تو در تو به یک سند تبدیل می‌شود.',
  },
  'org.ingest.format.irs990-officers.label': {
    en: 'IRS Form 990 officers (CSV)',
    fa: 'مسئولان فرم ۹۹۰ IRS (CSV)',
  },
  'org.ingest.format.irs990-officers.hint': {
    en: 'CSV with org_ein, org_name, name, title, compensation, hours, tax_year. Each row → officer OFFICER_OF organization.',
    fa: 'CSV با ستون‌های org_ein, org_name, name, title, compensation, hours, tax_year. هر سطر → افسر OFFICER_OF سازمان.',
  },
  'org.ingest.format.irs990-grants.label': {
    en: 'IRS Form 990 grants (CSV)',
    fa: 'کمک‌های فرم ۹۹۰ IRS (CSV)',
  },
  'org.ingest.format.irs990-grants.hint': {
    en: 'CSV with grantor_name, recipient, amount, purpose, tax_year. Each row → grantor GRANTED_TO recipient.',
    fa: 'CSV با ستون‌های grantor_name, recipient, amount, purpose, tax_year. هر سطر → اهداکننده GRANTED_TO گیرنده.',
  },
  'org.ingest.format.title': {
    en: 'Format & limits',
    fa: 'قالب و محدودیت‌ها',
  },
  'org.ingest.format.canonical.shape': {
    en: 'Upload a JSON file matching the corpus shape below — entities and edges already extracted by your pipeline (no NER on our side). Extra props on edges (year, confidence, custom fields) pass through to the graph as relationship properties.',
    fa: 'یک فایل JSON با ساختار زیر بارگذاری کنید — موجودیت‌ها و یال‌ها از قبل توسط خط‌لولهٔ شما استخراج شده‌اند (ما NER انجام نمی‌دهیم). ویژگی‌های اضافی روی یال‌ها (سال، اطمینان، فیلدهای سفارشی) به‌عنوان ویژگی‌های ارتباط به گراف منتقل می‌شوند.',
  },
  'org.ingest.format.fec.shape': {
    en: 'Raw FEC contributions JSON. Each item in `contributions[]` becomes one Document. Donor → committee edges are minted automatically; if `donor_employer` is present, an extra donor → employer edge is added. Address, occupation, amount, and date pass through as edge properties.',
    fa: 'JSON خام کمک‌های FEC. هر آیتم در `contributions[]` به یک سند تبدیل می‌شود. یال‌های اهداکننده → کمیته به‌طور خودکار ساخته می‌شوند؛ اگر `donor_employer` موجود باشد، یال اضافی اهداکننده → کارفرما هم اضافه می‌شود. آدرس، شغل، مبلغ و تاریخ به‌عنوان ویژگی‌های یال منتقل می‌شوند.',
  },
  'org.ingest.format.fec-identities.shape': {
    en: 'FEC donor index — top-level keys are donor names; each value is an array of identity records (different employer/occupation/location for the same name), and each identity carries its own nested `contributions[]`. The adapter walks down to the per-contribution records and emits one Document per contribution. The same donor → committee + EMPLOYED_BY edges are minted as the flat `fec-contributions` format.',
    fa: 'نمایهٔ اهداکنندگان FEC — کلیدهای سطح بالا نام اهداکنندگان هستند؛ هر مقدار آرایه‌ای از رکوردهای هویت است (کارفرما/شغل/مکان متفاوت برای همان نام)، و هر هویت آرایهٔ تو در توی `contributions[]` خود را دارد. آداپتور به سطح رکوردهای کمک می‌رود و برای هر کمک یک سند تولید می‌کند. همان یال‌های اهداکننده → کمیته + EMPLOYED_BY قالب تخت `fec-contributions` ساخته می‌شوند.',
  },
  'org.ingest.format.irs990-officers.shape': {
    en: 'IRS Form 990 "officers + key employees" CSV. Each row becomes one Document and one Person OFFICER_OF Organization edge. Title, compensation, hours, and tax year pass through as edge properties.',
    fa: 'CSV «مدیران + کارکنان کلیدی» فرم ۹۹۰ IRS. هر سطر به یک سند و یک یال شخص OFFICER_OF سازمان تبدیل می‌شود. عنوان، حقوق، ساعت‌ها و سال مالی به‌عنوان ویژگی‌های یال منتقل می‌شوند.',
  },
  'org.ingest.format.irs990-grants.shape': {
    en: 'IRS Form 990 "grants paid" CSV. Each row becomes one Document and one Organization GRANTED_TO Organization edge. Amount, purpose, and tax year pass through as edge properties.',
    fa: 'CSV «کمک‌های اعطایی» فرم ۹۹۰ IRS. هر سطر به یک سند و یک یال سازمان GRANTED_TO سازمان تبدیل می‌شود. مبلغ، هدف و سال مالی به‌عنوان ویژگی‌های یال منتقل می‌شوند.',
  },
  'org.ingest.format.size': {
    en: 'Max file size: {maxMb} MB. Max documents per run: {maxDocs}.',
    fa: 'حداکثر حجم فایل: {maxMb} مگابایت. حداکثر اسناد در هر اجرا: {maxDocs}.',
  },
  'org.ingest.format.shape-note': {
    en: 'Re-imports are idempotent — a row with the same content updates the existing document instead of duplicating. (System-corpus documents are read-only to org imports; only your own org\'s data can be overwritten.)',
    fa: 'ورود مجدد ایدئم‌پوتنت است — سطری با همان محتوا، سند موجود را به‌جای تکرار به‌روزرسانی می‌کند. (اسناد مجموعهٔ سیستمی برای ورودهای سازمانی فقط‌خواندنی هستند؛ تنها داده‌های سازمان خودتان قابل بازنویسی است.)',
  },
  'org.ingest.format.cli': {
    en: 'For corpora larger than the upload limit, use the CLI from a workstation with Neo4j credentials:',
    fa: 'برای مجموعه‌های داده بزرگ‌تر از محدودیت بارگذاری، از CLI روی یک ایستگاه کاری با اعتبارنامه‌های Neo4j استفاده کنید:',
  },
  'org.ingest.format.auto-detected': {
    en: 'auto-detected',
    fa: 'تشخیص خودکار',
  },
  'org.ingest.format.auto-detected.tooltip': {
    en: 'Source format was auto-selected from the picked file. Override via the dropdown if it guessed wrong.',
    fa: 'قالب منبع به‌طور خودکار از روی فایل انتخابی تنظیم شد. در صورت اشتباه، از منوی کشویی تغییر دهید.',
  },
  'org.ingest.submitted.title': {
    en: 'Import submitted — you can navigate away',
    fa: 'ورود ارسال شد — می‌توانید از این صفحه خارج شوید',
  },
  'org.ingest.submitted.toast-body': {
    en: '{fileName} is processing server-side. Pick another file or open Run detail to watch progress.',
    fa: '{fileName} روی سرور در حال پردازش است. می‌توانید فایل دیگری انتخاب کنید یا جزئیات اجرا را باز کنید.',
  },
  'org.ingest.submitted.cta': {
    en: 'Open run detail →',
    fa: '← باز کردن جزئیات اجرا',
  },
  'org.ingest.format.example-annotated': {
    en: 'Example below is annotated for clarity (`// comments` and `...` placeholders) — copy your real data into a valid file, don\'t paste this snippet verbatim.',
    fa: 'مثال زیر برای وضوح حاشیه‌نویسی شده است (`// توضیحات` و `...` نگه‌دارنده‌ها) — داده‌های واقعی خود را در یک فایل معتبر کپی کنید، این قطعه را عیناً کپی نکنید.',
  },
  'org.ingest.history.title': { en: 'Recent imports', fa: 'ورودهای اخیر' },
  'org.ingest.history.empty': {
    en: 'No imports yet for this organization.',
    fa: 'هنوز ورودی برای این سازمان ثبت نشده است.',
  },
  'org.ingest.history.docs': { en: 'docs', fa: 'سند' },
  'org.ingest.history.nodes': { en: 'nodes', fa: 'گره' },
  'org.ingest.history.edges': { en: 'edges', fa: 'یال' },
  'org.ingest.history.errors': { en: 'errors', fa: 'خطا' },
  'org.ingest.history.status.running': { en: 'running', fa: 'در حال اجرا' },
  'org.ingest.history.status.completed': { en: 'completed', fa: 'تکمیل‌شده' },
  'org.ingest.history.status.completed_with_errors': {
    en: 'completed w/ errors',
    fa: 'تکمیل با خطا',
  },
  'org.ingest.history.status.failed': { en: 'failed', fa: 'شکست‌خورده' },
  'org.ingest.history.status.canceled': { en: 'canceled', fa: 'لغو شد' },
  'org.ingest.history.status.stale': {
    en: 'stalled (timed out)',
    fa: 'متوقف‌شده (وقت تمام)',
  },
  'org.ingest.error.too-large': {
    en: 'File exceeds the {max} MB upload limit. Use the CLI for larger corpora (see Format & limits above).',
    fa: 'فایل از محدودیت بارگذاری {max} مگابایت تجاوز می‌کند. برای مجموعه‌های بزرگ‌تر از CLI استفاده کنید (به بخش قالب و محدودیت‌ها بالا مراجعه کنید).',
  },
  'org.switch': { en: 'Switch organization', fa: 'تعویض سازمان' },
  'org.noMemberships': {
    en: 'You are not a member of any organization yet.',
    fa: 'هنوز عضو هیچ سازمانی نیستید.',
  },
  'org.inviteAccept': { en: 'Accept invite', fa: 'پذیرش دعوت' },
  'org.inviteExpired': {
    en: 'This invite has expired or been revoked.',
    fa: 'این دعوت‌نامه منقضی یا لغو شده است.',
  },
  'org.lastAdminWarning': {
    en: 'You are the last admin. Promote another member before stepping down.',
    fa: 'شما تنها مدیر هستید. پیش از کناره‌گیری، یک عضو دیگر را به مدیر ارتقا دهید.',
  },
  'org.emailPinned': {
    en: 'This invite is pinned to a specific email address.',
    fa: 'این دعوت‌نامه به یک ایمیل مشخص محدود شده است.',
  },
  'org.generateInvite': { en: 'Generate invite', fa: 'ساخت دعوت‌نامه' },
  'org.copyLink': { en: 'Copy link', fa: 'کپی لینک' },
  'org.revoke': { en: 'Revoke', fa: 'لغو' },
  'org.grantSuperAdmin': {
    en: 'Grant super admin',
    fa: 'اعطای دسترسی مدیر کل',
  },
  'org.revokeSuperAdmin': {
    en: 'Revoke super admin',
    fa: 'لغو دسترسی مدیر کل',
  },
  'org.name': { en: 'Organization name', fa: 'نام سازمان' },
  'org.slug': { en: 'Slug', fa: 'شناسه URL' },
  'org.slug.help': {
    en: 'Lowercase letters, numbers, and hyphens. Used in URLs.',
    fa: 'حروف کوچک، اعداد و خط تیره. در آدرس‌ها استفاده می‌شود.',
  },
  'org.save': { en: 'Save', fa: 'ذخیره' },
  'org.saving': { en: 'Saving…', fa: '…در حال ذخیره' },
  'org.saved': { en: 'Saved', fa: 'ذخیره شد' },
  'org.role.admin': { en: 'Admin', fa: 'مدیر' },
  'org.role.member': { en: 'Member', fa: 'عضو' },
  'org.promote': { en: 'Promote to admin', fa: 'ارتقا به مدیر' },
  'org.demote': { en: 'Demote to member', fa: 'تنزل به عضو' },
  'org.removeMember': { en: 'Remove', fa: 'حذف' },
  'org.removeConfirm': {
    en: 'Remove this member from the organization?',
    fa: 'این عضو از سازمان حذف شود؟',
  },
  'org.invites.empty': {
    en: 'No pending invites.',
    fa: 'دعوت‌نامه‌ای در انتظار نیست.',
  },
  'org.invites.email': { en: 'Email (optional)', fa: 'ایمیل (اختیاری)' },
  'org.invites.ttlDays': { en: 'Expires in (days)', fa: 'انقضا (روز)' },
  'org.invites.qr': { en: 'Show QR code', fa: 'نمایش کد QR' },
  'org.members.empty': {
    en: 'No members yet.',
    fa: 'هنوز عضوی وجود ندارد.',
  },
  'org.created': { en: 'Created', fa: 'زمان ساخت' },
  'org.expires': { en: 'Expires', fa: 'انقضا' },
  'org.linkCopied': { en: 'Link copied', fa: 'لینک کپی شد' },
  // Platform superadmin.
  'superadmin.section': { en: 'Platform admin', fa: 'مدیر سکو' },
  'superadmin.orgs': { en: 'Organizations', fa: 'سازمان‌ها' },
  'superadmin.users': { en: 'Users', fa: 'کاربران' },
  'superadmin.newOrg': { en: 'New organization', fa: 'سازمان جدید' },
  'superadmin.orgs.empty': {
    en: 'No organizations yet.',
    fa: 'هنوز سازمانی ثبت نشده است.',
  },
  'superadmin.users.empty': {
    en: 'No users yet.',
    fa: 'هنوز کاربری ثبت نشده است.',
  },
  'superadmin.firstAdminUid': {
    en: 'First admin UID (optional — defaults to you)',
    fa: 'شناسه اولین مدیر (اختیاری — پیش‌فرض: خودتان)',
  },
  'superadmin.createCta': { en: 'Create organization', fa: 'ساخت سازمان' },
  'superadmin.role.superAdmin': { en: 'superAdmin', fa: 'مدیر کل' },
  'superadmin.revokeSelf': { en: 'revoke self', fa: 'لغو دسترسی خود' },
  'org.col.user': { en: 'User', fa: 'کاربر' },
  'org.col.uid': { en: 'UID', fa: 'شناسه' },
  'org.col.email': { en: 'Email', fa: 'ایمیل' },
  'org.col.role': { en: 'Role', fa: 'نقش' },
  'org.col.member': { en: 'Member', fa: 'عضو' },
  'org.col.platformRole': { en: 'Platform role', fa: 'دسترسی سکو' },
  'org.settings.emailDomains': {
    en: 'Email domain allowlist',
    fa: 'دامنه‌های ایمیل مجاز',
  },
  'org.settings.emailDomains.help': {
    en: 'Comma-separated. Empty = any domain.',
    fa: 'با کاما جدا کنید. خالی = بدون محدودیت.',
  },
  'org.settings.defaultLang': {
    en: 'Default language',
    fa: 'زبان پیش‌فرض',
  },
  'org.settings.timezone': { en: 'Timezone', fa: 'منطقه زمانی' },
  'org.settings.timezone.currentSaved': {
    en: '{tz} (current saved value)',
    fa: '{tz} (مقدار ذخیره‌شدهٔ فعلی)',
  },
  'org.invite.status.active': { en: 'Active', fa: 'فعال' },
  'org.invite.status.consumed': { en: 'Accepted', fa: 'پذیرفته شد' },
  'org.invite.status.revoked': { en: 'Revoked', fa: 'لغو شد' },
  'org.invite.status.expired': { en: 'Expired', fa: 'منقضی' },
  'org.invite.welcome': {
    en: 'You have been invited to {name}',
    fa: 'شما به «{name}» دعوت شده‌اید',
  },
  'common.cancel': { en: 'Cancel', fa: 'انصراف' },
  'common.back': { en: 'Back', fa: 'بازگشت' },
  // Audit explorer (superAdmin).
  'superadmin.audit': { en: 'Audit log', fa: 'گزارش حسابرسی' },
  'superadmin.audit.title': { en: 'Audit log', fa: 'گزارش حسابرسی' },
  'superadmin.audit.subtitle': {
    en: 'Cross-tenant forensic record. Every mutation, every share, every membership change. Privacy-sensitive context (IP hash, country, device) is stored separately and auto-redacted after 90 days.',
    fa: 'گزارش حسابرسی میان‌سازمانی. هر تغییر، هر اشتراک‌گذاری و هر جابجایی عضویت ثبت می‌شود. اطلاعات حساس (هش IP، کشور، دستگاه) جداگانه نگهداری و پس از ۹۰ روز به‌طور خودکار حذف می‌شوند.',
  },
  'superadmin.audit.privacy_note': {
    en: 'Action + actor only — no IP/device shown',
    fa: 'فقط اقدام و کنشگر — IP و دستگاه نمایش داده نمی‌شود',
  },
  'superadmin.audit.filter.org': { en: 'Organization', fa: 'سازمان' },
  'superadmin.audit.filter.org.all': {
    en: 'All organizations',
    fa: 'همه سازمان‌ها',
  },
  'superadmin.audit.filter.org.platform': {
    en: 'Platform-level only',
    fa: 'فقط سطح سکو',
  },
  'superadmin.audit.filter.category': { en: 'Category', fa: 'دسته' },
  'superadmin.audit.filter.search': { en: 'Search', fa: 'جستجو' },
  'superadmin.audit.filter.search.placeholder': {
    en: 'Filter by action, target, or email…',
    fa: 'فیلتر با اقدام، هدف یا ایمیل…',
  },
  'superadmin.audit.empty': {
    en: 'No audit entries match the current filters.',
    fa: 'هیچ گزارشی با فیلترهای فعلی یافت نشد.',
  },
  'superadmin.audit.error': {
    en: 'Could not load audit entries. Confirm you have superAdmin access and try again.',
    fa: 'بارگیری گزارش‌ها ممکن نشد. لطفاً مطمئن شوید دسترسی مدیر کل دارید و دوباره تلاش کنید.',
  },
  'superadmin.audit.col.at': { en: 'When', fa: 'زمان' },
  'superadmin.audit.col.actor': { en: 'Actor', fa: 'کنشگر' },
  'superadmin.audit.col.org': { en: 'Org', fa: 'سازمان' },
  'superadmin.audit.col.action': { en: 'Action', fa: 'اقدام' },
  'superadmin.audit.col.target': { en: 'Target', fa: 'هدف' },
  'superadmin.audit.col.severity': { en: 'Severity', fa: 'شدت' },
  'superadmin.audit.org.public': {
    en: 'public',
    fa: 'عمومی',
  },
  'superadmin.audit.row_count': {
    en: 'Showing {count} entries',
    fa: 'نمایش {count} مورد',
  },
  'superadmin.audit.detail.title': {
    en: 'Event details',
    fa: 'جزئیات رویداد',
  },
  'superadmin.audit.detail.country': { en: 'Country', fa: 'کشور' },
  'superadmin.audit.detail.device': { en: 'Device', fa: 'دستگاه' },
  'superadmin.audit.detail.payload': { en: 'Payload', fa: 'داده' },
  // Onboarding.
  'onboarding.selectOrg.prompt': {
    en: 'Pick an organization to continue.',
    fa: 'برای ادامه، یک سازمان را انتخاب کنید.',
  },
  'onboarding.selectOrg.continue': { en: 'Continue', fa: 'ادامه' },
  'onboarding.noOrg.title': {
    en: 'No organization yet',
    fa: 'هنوز عضو هیچ سازمانی نیستید',
  },
  'onboarding.noOrg.message': {
    en: 'Ask an admin to invite you. Once you accept, this page will hand off to your workspace.',
    fa: 'از مدیر بخواهید شما را دعوت کند. پس از پذیرش، به فضای کاری‌تان منتقل می‌شوید.',
  },
} as const satisfies Record<string, Record<Lang, string>>;

export type StringKey = keyof typeof STRINGS;

export function t(key: StringKey, lang: Lang): string {
  return STRINGS[key][lang];
}

/**
 * Substitute `{name}` placeholders in a STRINGS entry. Centralizes the
 * `t(...).replace('{n}', String(v))` idiom that the codebase had begun
 * to scatter — new placeholder-bearing strings should reach for this
 * instead. Existing call sites are left as-is and can be migrated
 * incrementally.
 */
export function tFormat(
  key: StringKey,
  lang: Lang,
  vars: Record<string, string | number>,
): string {
  // Annotate `out: string` — STRINGS is `as const`, so the lookup
  // returns a literal type that narrows the var and rejects subsequent
  // `replaceAll` returns. Widening once at the entry keeps the loop sane.
  let out: string = STRINGS[key][lang];
  for (const [k, v] of Object.entries(vars)) {
    out = out.replaceAll(`{${k}}`, String(v));
  }
  return out;
}

/** Format a number with the user's preferred numeral system. */
export function formatNumber(n: number, lang: Lang, numerals: Numerals): string {
  const locale = lang === 'fa' ? `fa-IR-u-nu-${numerals}` : `en-US-u-nu-${numerals}`;
  return new Intl.NumberFormat(locale).format(n);
}

/** True if a string appears to contain Persian/Arabic characters. */
export function isRtlText(s: string): boolean {
  return /[֑-߿יִ-﷽ﹰ-ﻼ]/.test(s);
}
